import React, { useCallback, useEffect, useMemo, useState, Component } from 'react';
import { X, Banknote, CreditCard, Smartphone, Loader2, Receipt, CheckSquare, Square, AlertCircle, CheckCircle2, Mail, Printer, ArrowLeft, FileText, Minus, Plus, Clock } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn, formatCurrency } from '../../lib/utils';
import { useTableOrderItems } from '../../lib/table-hooks';
import { supabase } from '../../lib/supabase';
import { useBillingInfo, processPayment, openPrintableInvoice, PaymentItemSnapshot, PaymentRecord, BillingInfo } from '../../lib/billing-hooks';
import { TableWithOrder } from '../../lib/types';
// ─── DEPLOY CHECK (temporal) ────────────────────────────────────────
// Se imprime UNA vez al cargar el bundle. Si tras hard-refresh
// (Ctrl+Shift+R) este log NO aparece en la consola, producción todavía
// está sirviendo el bundle viejo (problema de build/caché, no de código).
// Quitar este marcador una vez confirmado el deploy.
console.log('[NOIZA][DEPLOY_CHECK] invoice-customer-payload-v2 ACTIVE');
interface PayTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  table: TableWithOrder | null;
  restaurantId: number | string | undefined;
  onSuccess: (allPaid: boolean) => void;
  createdBy?: string | null;
}
type PayMethod = 'cash' | 'sinpe' | 'card';
type Step = 'select' | 'paid' | 'invoice';
// ─── Helper: división equitativa con partes pagadas bloqueadas ────────
// Devuelve un array de longitud N con el monto (entero) de cada parte.
// Las partes ya pagadas conservan su monto real (paidAmounts); el resto
// del total se reparte equitativamente entre las partes NO pagadas, y el
// remanente de redondeo cae en la última parte no pagada. Garantiza que
// la suma de TODAS las partes sea exactamente igual a unitPrice.
function computeEqualSplit(unitPrice: number, N: number, paidParts: Set<number>, paidAmounts: Map<number, number>): number[] {
  const total = Math.round(unitPrice);
  const arr = new Array<number>(N).fill(0);
  let paidSum = 0;
  paidParts.forEach(p => {
    if (p >= 0 && p < N) {
      const amt = Math.round(paidAmounts.get(p) ?? 0);
      arr[p] = amt;
      paidSum += amt;
    }
  });
  const unpaid: number[] = [];
  for (let k = 0; k < N; k++) {
    if (!paidParts.has(k)) unpaid.push(k);
  }
  const budget = Math.max(0, total - paidSum);
  if (unpaid.length > 0) {
    const base = Math.floor(budget / unpaid.length);
    const rem = budget - base * unpaid.length;
    unpaid.forEach((idx, i) => {
      arr[idx] = base + (i === unpaid.length - 1 ? rem : 0);
    });
  }
  return arr;
}
function ArmaPriceInput({
  value,
  onChange,
  needsPrice,
  itemName
}: {
  value: string;
  onChange: (val: string) => void;
  needsPrice: boolean;
  itemName: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <>
      <button type="button" onClick={() => setIsOpen(true)} className={cn('relative w-28 pl-5 pr-2 py-1.5 rounded-md border-2 text-sm font-black tabular-nums text-right focus:outline-none transition-colors', needsPrice ? 'border-red-300 focus:border-red-500 text-red-700 bg-white' : 'border-amber-300 focus:border-amber-500 text-gray-900 bg-white')} aria-label={`Precio de ${itemName}`} data-id="element-1184">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-black text-amber-600 pointer-events-none" data-id="element-1185">
          ₡
        </span>
        {value || <span className={needsPrice ? 'text-red-300' : 'text-gray-300'} data-id="element-1186">
            Monto
          </span>}
      </button>

      {isOpen && <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-id="element-1187">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xs overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-1188">
            <div className="p-4 bg-gray-50 border-b flex items-center justify-between" data-id="element-1189">
              <h3 className="font-bold text-gray-700 text-sm truncate pr-4" data-id="element-1190">
                Monto: {itemName}
              </h3>
              <button type="button" onClick={() => setIsOpen(false)} className="p-2 -mr-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-200 transition-colors" data-id="element-1191">
                <X className="w-5 h-5" data-id="element-1192" />
              </button>
            </div>
            <div className="p-6" data-id="element-1193">
              <div className="text-center mb-6" data-id="element-1194">
                <div className="text-3xl font-black text-gray-900 tabular-nums tracking-tight" data-id="element-1195">
                  ₡ {value || '0'}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3" data-id="element-1196">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => <button key={num} type="button" onClick={() => onChange(value + num.toString())} className="h-14 rounded-2xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-xl font-bold text-gray-900 transition-colors shadow-sm border border-gray-100" data-id="element-1197">
                    {num}
                  </button>)}
                <button type="button" onClick={() => onChange('')} className="h-14 rounded-2xl bg-red-50 hover:bg-red-100 active:bg-red-200 text-sm font-bold text-red-600 transition-colors shadow-sm border border-red-100 flex items-center justify-center" data-id="element-1198">
                  Borrar
                </button>
                <button type="button" onClick={() => onChange(value + '0')} className="h-14 rounded-2xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-xl font-bold text-gray-900 transition-colors shadow-sm border border-gray-100" data-id="element-1199">
                  0
                </button>
                <button type="button" onClick={() => onChange(value + '00')} className="h-14 rounded-2xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-lg font-bold text-gray-900 transition-colors shadow-sm border border-gray-100" data-id="element-1200">
                  00
                </button>
              </div>
              <button type="button" onClick={() => setIsOpen(false)} className="w-full mt-4 h-14 rounded-2xl bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white font-bold text-lg transition-colors shadow-md" data-id="element-1201">
                Aceptar
              </button>
            </div>
          </div>
        </div>}
    </>;
}
export function PayTableModal({
  isOpen,
  onClose,
  table,
  restaurantId,
  onSuccess,
  createdBy
}: PayTableModalProps) {
  const orderId = table?.current_order?.id;
  // Pass orderId as a fallback so marketplace orders (no real table) can
  // still load their items for Cobrar/Facturar.
  const {
    items,
    loading: itemsLoading,
    refetch
  } = useTableOrderItems(table?.id ? String(table.id) : undefined, restaurantId ? String(restaurantId) : undefined, orderId ? String(orderId) : undefined);
  const {
    info: billingInfo,
    loading: infoLoading
  } = useBillingInfo(restaurantId);
  const [step, setStep] = useState<Step>('select');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [method, setMethod] = useState<PayMethod>('cash');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completedPayment, setCompletedPayment] = useState<PaymentRecord | null>(null);
  const [allPaid, setAllPaid] = useState(false);
  // ─── Auto-send invoice result ─────────────────────────────────────────
  // Cuando el cliente activa "Solicitar factura" + "Enviar ahora" y hay
  // correo, se invoca la edge function send-invoice-email INMEDIATAMENTE
  // después de insertar el payment. El resultado se guarda acá para
  // mostrar feedback en el paso "Pago registrado". Si falla, el flujo
  // cae al paso 'invoice' clásico para que el cajero pueda reintentar
  // manualmente — el pago ya quedó registrado.
  const [autoInvoiceResult, setAutoInvoiceResult] = useState<{
    ok: boolean;
    msg: string;
    email: string;
  } | null>(null);
  const [autoInvoiceSending, setAutoInvoiceSending] = useState(false);
  // ─── Invoice toggle + customer data (collected BEFORE payment) ─────────
  // El usuario puede activar "Solicitar factura" desde el primer paso para
  // ingresar nombre / cédula / correo. Si está activo, después de cobrar
  // saltamos directo al paso de factura (sin pasar por el confirm "paid").
  const [wantsInvoice, setWantsInvoice] = useState(false);
  // sendLater: cuando wantsInvoice=true, el cajero puede elegir "Enviar
  // ahora" (flujo actual → salta al paso de factura) o "Enviar más tarde"
  // (guarda los datos en orders.wants_invoice + invoice_* y la factura
  // queda pendiente en "Otras Herramientas → Enviar Facturas").
  const [sendLater, setSendLater] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerLegalId, setCustomerLegalId] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  // ─── Imprimir comprobante con datos del cliente (NatuCafé) ─────────
  // Independiente del envío de factura por correo. Cuando está activo,
  // el print job incluye show_customer_on_invoice=true + nombre + cédula
  // para que el bridge los imprima. Cuando está inactivo, la factura
  // impresa sale EXACTAMENTE igual que hoy (sin datos del cliente).
  const [showCustomerOnInvoice, setShowCustomerOnInvoice] = useState(false);
  // ─── Estado DEDICADO del comprobante impreso (NatuCafé id 19) ──────
  // CRÍTICO: estos dos estados son EXCLUSIVOS del bloque "Imprimir
  // comprobante con datos del cliente". NO se reutiliza `customerName`
  // (que en mesas representa "Mesa 01" / el form de correo) para evitar
  // que el payload de la factura impresa arrastre el nombre de la mesa.
  // Los inputs del comprobante y el payload de invoice se controlan
  // SOLO con estos estados.
  const [invoiceCustomerName, setInvoiceCustomerName] = useState('');
  const [invoiceCustomerId, setInvoiceCustomerId] = useState('');
  // ─── Discount (universal — works for all KDS) ──────────────────────
  // El cajero puede asignar un descuento al cobro completo (no por línea).
  // Se aplica como una línea sintética negativa al snapshot del pago, lo
  // que hace que aparezca automáticamente en el ticket impreso
  // (openPrintableInvoice), en el correo de factura
  // (send-invoice-email itera items_json) y en el ticket térmico del
  // bridge NatuCafé (format_invoice_ticket_natucafe itera payload.items).
  // Además persistimos el monto en orders.discount_amount.
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [showDiscountPanel, setShowDiscountPanel] = useState(false);
  // ─── Quantity-based partial pay ────────────────────────────────────
  // Cada línea de order_items tiene quantity N. Permitimos cobrar una
  // cantidad parcial dentro de la línea (ej: 2 de 3 capuccinos). El
  // saldo restante queda disponible para próximos cobros.
  //
  // paidQtyMap: cuántas unidades de cada order_item ya se cobraron en
  // pagos anteriores (suma de payments.items_json[].quantity).
  // selectedQty: cuántas unidades de cada order_item se van a cobrar
  // en ESTE pago. Default = remaining_quantity al seleccionar.
  const [paidQtyMap, setPaidQtyMap] = useState<Map<string, number>>(new Map());
  const [selectedQty, setSelectedQty] = useState<Map<string, number>>(new Map());
  // ─── Split a single shareable item ─────────────────────────────────
  const [persistedSplits, setPersistedSplits] = useState<Map<string, {
    N: number;
    paidParts: Set<number>;
    // Monto real cobrado por cada parte ya pagada (index → ₡).
    // Necesario para recalcular correctamente el restante de las
    // partes NO pagadas cuando el cajero edita montos manualmente.
    paidAmounts: Map<number, number>;
  }>>(new Map());
  const [splitModeMap, setSplitModeMap] = useState<Map<string, number>>(new Map());
  const [splitSelectedMap, setSplitSelectedMap] = useState<Map<string, Set<number>>>(new Map());
  // Monto editable de cada parte por order_item (array de longitud N).
  // Default = división equitativa; el cajero puede sobreescribir cada
  // parte no pagada y el restante se reparte automáticamente.
  const [splitAmountsMap, setSplitAmountsMap] = useState<Map<string, number[]>>(new Map());
  // ─── "Arma tu plato" — PRECIO INDIVIDUAL por cada línea ───────────
  // Cada línea de "Arma tu plato" (menu_item_id == null) es un ítem
  // independiente: el cajero le asigna su propio monto y elige cuáles
  // cobrar. armaPriceMap guarda el texto crudo del input por
  // order_item_id (se parsea a número en getArmaPriceNum).
  const [armaPriceMap, setArmaPriceMap] = useState<Map<string, string>>(new Map());
  const setArmaPrice = useCallback((id: string, value: string) => {
    const clean = value.replace(/[^0-9.]/g, '');
    setArmaPriceMap(prev => {
      const nm = new Map(prev);
      nm.set(id, clean);
      return nm;
    });
  }, []);
  // Carga la suma de cantidades ya cobradas por order_item desde la tabla
  // payments. Es seguro de re-correr — solo lee.
  const refetchPaidQty = useCallback(async () => {
    if (!orderId || !restaurantId) {
      setPaidQtyMap(new Map());
      setPersistedSplits(new Map());
      return;
    }
    try {
      const {
        data: pastPayments,
        error
      } = await supabase.from('payments').select('items_json').eq('order_id', orderId);
      if (error) {
        console.warn('[NOIZA] paidQty fetch error:', error);
        setPaidQtyMap(new Map());
        setPersistedSplits(new Map());
        return;
      }
      const map = new Map<string, number>();
      const splitMap = new Map<string, {
        N: number;
        paidParts: Set<number>;
        paidAmounts: Map<number, number>;
      }>();
      for (const p of pastPayments || []) {
        const arr = (p as any).items_json || [];
        for (const it of arr) {
          if (!it?.order_item_id) continue;
          const prev = map.get(it.order_item_id) || 0;
          map.set(it.order_item_id, prev + (Number(it.quantity) || 0));
          if (it.notes && typeof it.notes === 'string') {
            const match = it.notes.match(/Parte (\d+)\/(\d+)/);
            if (match) {
              const k = parseInt(match[1], 10) - 1; // 0-indexed
              const N = parseInt(match[2], 10);
              if (!splitMap.has(it.order_item_id)) {
                splitMap.set(it.order_item_id, {
                  N,
                  paidParts: new Set(),
                  paidAmounts: new Map()
                });
              }
              const entry = splitMap.get(it.order_item_id)!;
              entry.paidParts.add(k);
              // Guardamos el monto real cobrado de esa parte (line_total).
              entry.paidAmounts.set(k, Math.round(Number(it.line_total) || 0));
            }
          }
        }
      }
      setPaidQtyMap(map);
      setPersistedSplits(splitMap);
    } catch (err) {
      console.warn('[NOIZA] paidQty fetch exception:', err);
      setPaidQtyMap(new Map());
      setPersistedSplits(new Map());
    }
  }, [orderId, restaurantId]);
  useEffect(() => {
    if (isOpen) refetchPaidQty();
  }, [isOpen, refetchPaidQty, items.length]);
  // Side-fetch order_items.menu_item_id keyed by order_item_id. We can't
  // rely on v_table_order_items.menu_item_id because the view doesn't
  // expose that column — which makes every item look like an arma item.
  // This map is the source of truth for arma detection in the UI.
  const [menuItemIdMap, setMenuItemIdMap] = useState<Map<string, string | null>>(new Map());
  useEffect(() => {
    let cancelled = false;
    async function loadMenuItemIds() {
      if (!orderId) {
        setMenuItemIdMap(new Map());
        return;
      }
      try {
        const {
          data,
          error
        } = await supabase.from('order_items').select('id, menu_item_id').eq('order_id', orderId);
        if (cancelled) return;
        if (error) {
          console.warn('[NOIZA] menu_item_id map fetch error:', error);
          setMenuItemIdMap(new Map());
          return;
        }
        const map = new Map<string, string | null>();
        (data || []).forEach((row: any) => {
          map.set(String(row.id), row.menu_item_id ?? null);
        });
        setMenuItemIdMap(map);
      } catch (e) {
        if (cancelled) return;
        console.warn('[NOIZA] menu_item_id map fetch exception:', e);
        setMenuItemIdMap(new Map());
      }
    }
    if (isOpen) loadMenuItemIds();
    return () => {
      cancelled = true;
    };
  }, [isOpen, orderId, items.length]);
  // Items con quantity disponible para cobrar (remaining > 0). NOTA:
  // si paid_at ya está seteado, el ítem ya fue completamente pagado
  // por el flujo viejo (que marca paid_at al instante en cobro total) —
  // lo respetamos y lo dejamos fuera. Para los que tienen paid_at NULL
  // calculamos remaining = quantity - paid_so_far.
  const unpaidItems = useMemo(() => {
    return items.filter((i: any) => !i.paid_at).map((i: any) => {
      const paidSoFar = paidQtyMap.get(i.order_item_id) || 0;
      const remaining = Math.max(0, (i.quantity || 0) - paidSoFar);
      return {
        ...i,
        paid_quantity: paidSoFar,
        remaining_quantity: remaining
      };
    }).filter((i: any) => i.remaining_quantity > 0);
  }, [items, paidQtyMap]);
  // ─── BLINDAJE: precio por unidad CON recargos de opciones ──────────
  // El `unit_price` de la línea es el precio BASE del producto
  // (unit_price_snapshot) y NO incluye el sobreprecio de opciones como
  // "Costilla", "patacones extra", etc. Ese recargo SÍ está guardado en
  // `line_total` (lo calcula create-order.ts como (base + opciones) * qty).
  //
  // Si cobramos usando `unit_price` perdemos el recargo en la factura —
  // exactamente el bug de Rancho con "casado con costilla". Acá derivamos
  // el precio por unidad REAL desde el line_total persistido:
  //   precio_unitario_real = line_total / quantity
  //
  // Para productos SIN recargo, line_total/quantity == unit_price, así que
  // el resultado es idéntico al de hoy (cero cambio de comportamiento).
  // Las líneas "Arma tu plato" no pasan por acá (usan su precio manual).
  // IMPORTANTE: se define ANTES de cualquier efecto/función que lo use
  // para evitar el "temporal dead zone" (effUnitPrice is not defined).
  const effUnitPrice = useCallback((i: any): number => {
    const qty = Number(i?.quantity) || 0;
    const lt = Number(i?.line_total);
    if (qty > 0 && Number.isFinite(lt) && lt > 0) {
      return lt / qty;
    }
    return Number(i?.unit_price) || 0;
  }, []);
  useEffect(() => {
    if (isOpen && unpaidItems.length > 0 && step === 'select') {
      // Por defecto seleccionar TODOS los ítems con su remaining
      // completo. El cajero puede ajustar con [-]/[+] dentro de cada
      // línea o deseleccionar líneas completas.
      setSelectedIds(new Set(unpaidItems.map((i: any) => i.order_item_id)));
      setSelectedQty(new Map(unpaidItems.map((i: any) => [i.order_item_id, i.remaining_quantity] as [string, number])));
      const newSplitModeMap = new Map<string, number>();
      const newSplitSelectedMap = new Map<string, Set<number>>();
      const newSplitAmountsMap = new Map<string, number[]>();
      persistedSplits.forEach((val, key) => {
        newSplitModeMap.set(key, val.N);
        const selectedParts = new Set<number>();
        for (let k = 0; k < val.N; k++) {
          if (!val.paidParts.has(k)) selectedParts.add(k);
        }
        newSplitSelectedMap.set(key, selectedParts);
        // Reconstruimos los montos de cada parte: las pagadas conservan
        // su monto real (paidAmounts), las no pagadas se reparten el
        // saldo restante equitativamente.
        const itm = unpaidItems.find((i: any) => i.order_item_id === key);
        const unitPrice = itm ? effUnitPrice(itm) : 0;
        newSplitAmountsMap.set(key, computeEqualSplit(unitPrice, val.N, val.paidParts, val.paidAmounts));
      });
      setSplitModeMap(newSplitModeMap);
      setSplitSelectedMap(newSplitSelectedMap);
      setSplitAmountsMap(newSplitAmountsMap);
    }
    if (!isOpen) {
      setStep('select');
      setSelectedIds(new Set());
      setSelectedQty(new Map());
      setSplitModeMap(new Map());
      setSplitSelectedMap(new Map());
      setSplitAmountsMap(new Map());
      setError(null);
      setMethod('cash');
      setCompletedPayment(null);
      setAllPaid(false);
      setWantsInvoice(false);
      setSendLater(false);
      setCustomerName('');
      setCustomerLegalId('');
      setCustomerEmail('');
      setShowCustomerOnInvoice(false);
      setInvoiceCustomerName('');
      setInvoiceCustomerId('');
      setAutoInvoiceResult(null);
      setAutoInvoiceSending(false);
      setArmaPriceMap(new Map());
      setPrintComandaResult(null);
      setPrintingComanda(false);
      setDiscountAmount(0);
      setShowDiscountPanel(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, unpaidItems.length]);
  const toggleItem = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        // Al re-seleccionar, restablece la cantidad a remaining
        const itm = unpaidItems.find((i: any) => i.order_item_id === id);
        if (itm) {
          setSelectedQty(m => {
            const nm = new Map(m);
            nm.set(id, itm.remaining_quantity);
            return nm;
          });
        }
      }
      return next;
    });
  };
  const setQtyForItem = (id: string, newQty: number) => {
    const itm = unpaidItems.find((i: any) => i.order_item_id === id);
    if (!itm) return;
    const clamped = Math.max(1, Math.min(newQty, itm.remaining_quantity));
    setSelectedQty(m => {
      const nm = new Map(m);
      nm.set(id, clamped);
      return nm;
    });
  };
  // ─── Edición manual del monto de cada parte de un ítem dividido ─────
  // Reglas:
  //  · No negativos, no mayor al saldo disponible para partes no pagadas.
  //  · Al editar una parte, el restante se reparte automáticamente entre
  //    las OTRAS partes NO pagadas (remanente de redondeo a la última).
  //  · Las partes ya pagadas quedan bloqueadas y conservan su monto.
  //  · La suma de TODAS las partes siempre == total del ítem.
  const setPartAmount = (id: string, k: number, rawValue: number) => {
    const itm = unpaidItems.find((i: any) => i.order_item_id === id);
    if (!itm) return;
    const N = splitModeMap.get(id);
    if (!N) return;
    const persisted = persistedSplits.get(id);
    const paidParts = persisted?.paidParts ?? new Set<number>();
    const paidAmounts = persisted?.paidAmounts ?? new Map<number, number>();
    // No se puede editar una parte ya pagada.
    if (paidParts.has(k)) return;
    // Usamos effUnitPrice para incluir el recargo de opciones (Rancho).
    const unitPrice = Math.round(effUnitPrice(itm));
    let paidSum = 0;
    paidParts.forEach(p => {
      paidSum += Math.round(paidAmounts.get(p) ?? 0);
    });
    const unpaidBudget = Math.max(0, unitPrice - paidSum);
    const val = Math.max(0, Math.min(Math.round(rawValue || 0), unpaidBudget));
    setSplitAmountsMap(prev => {
      const nm = new Map(prev);
      const current = prev.get(id) ?? computeEqualSplit(unitPrice, N, paidParts, paidAmounts);
      const next = [...current];
      // Aseguramos que las partes pagadas mantengan su monto real.
      paidParts.forEach(p => {
        next[p] = Math.round(paidAmounts.get(p) ?? next[p] ?? 0);
      });
      next[k] = val;
      // Otras partes NO pagadas (distintas de k) entre las que repartir.
      const otherUnpaid: number[] = [];
      for (let idx = 0; idx < N; idx++) {
        if (idx !== k && !paidParts.has(idx)) otherUnpaid.push(idx);
      }
      const remaining = Math.max(0, unpaidBudget - val);
      if (otherUnpaid.length === 0) {
        // Esta es la única parte no pagada: debe cubrir todo el saldo.
        next[k] = unpaidBudget;
      } else {
        const base = Math.floor(remaining / otherUnpaid.length);
        const rem = remaining - base * otherUnpaid.length;
        otherUnpaid.forEach((idx, i) => {
          next[idx] = base + (i === otherUnpaid.length - 1 ? rem : 0);
        });
      }
      nm.set(id, next);
      return nm;
    });
  };
  // Inicializa/recalcula el array de montos en división equitativa para
  // un ítem (usado al activar "Dividir" o al cambiar la cantidad N).
  const seedEqualSplit = (id: string, N: number) => {
    const itm = unpaidItems.find((i: any) => i.order_item_id === id);
    const unitPrice = itm ? effUnitPrice(itm) : 0;
    const persisted = persistedSplits.get(id);
    const paidParts = persisted?.paidParts ?? new Set<number>();
    const paidAmounts = persisted?.paidAmounts ?? new Map<number, number>();
    setSplitAmountsMap(prev => {
      const nm = new Map(prev);
      nm.set(id, computeEqualSplit(unitPrice, N, paidParts, paidAmounts));
      return nm;
    });
  };
  const allSelected = useMemo(() => {
    if (unpaidItems.length === 0) return false;
    return unpaidItems.every((i: any) => {
      if (splitModeMap.has(i.order_item_id)) {
        const N = splitModeMap.get(i.order_item_id)!;
        const selectedParts = splitSelectedMap.get(i.order_item_id) || new Set();
        const persistedSplit = persistedSplits.get(i.order_item_id);
        const paidCount = persistedSplit?.paidParts.size || 0;
        return selectedParts.size + paidCount === N;
      }
      const qty = selectedQty.get(i.order_item_id) ?? i.remaining_quantity;
      return selectedIds.has(i.order_item_id) && qty === i.remaining_quantity;
    });
  }, [unpaidItems, selectedIds, selectedQty, splitModeMap, splitSelectedMap, persistedSplits]);
  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
      const newSplitSelectedMap = new Map<string, Set<number>>();
      splitModeMap.forEach((N, id) => {
        newSplitSelectedMap.set(id, new Set());
      });
      setSplitSelectedMap(newSplitSelectedMap);
    } else {
      setSelectedIds(new Set(unpaidItems.map((i: any) => i.order_item_id)));
      setSelectedQty(new Map(unpaidItems.map((i: any) => [i.order_item_id, i.remaining_quantity] as [string, number])));
      const newSplitSelectedMap = new Map<string, Set<number>>();
      splitModeMap.forEach((N, id) => {
        const persistedSplit = persistedSplits.get(id);
        const parts = new Set<number>();
        for (let k = 0; k < N; k++) {
          if (!persistedSplit?.paidParts.has(k)) {
            parts.add(k);
          }
        }
        newSplitSelectedMap.set(id, parts);
      });
      setSplitSelectedMap(newSplitSelectedMap);
    }
  };
  // selectedItems lleva ahora la cantidad efectiva a cobrar y el
  // line_total recalculado en base a la cantidad seleccionada (no la
  // total de la línea original).
  // Precio numérico individual de una línea "Arma tu plato".
  const getArmaPriceNum = useCallback((id: string) => Number(String(armaPriceMap.get(id) ?? '').replace(/[^0-9.]/g, '')) || 0, [armaPriceMap]);
  // ─── Helper used everywhere the UI classifies arma items.
  //     Authoritative source: menuItemIdMap from order_items table.
  const isArmaItem = useCallback((orderItemId: string) => {
    const realMenuItemId = menuItemIdMap.get(String(orderItemId));
    // Only treat as arma when we KNOW the menu_item_id is null. If the
    // map hasn't loaded yet (undefined), default to NOT arma so we don't
    // misclassify normal items as arma during the brief loading window.
    return realMenuItemId === null;
  }, [menuItemIdMap]);
  const normalSelectedItems = unpaidItems.filter((i: any) => selectedIds.has(i.order_item_id) && !splitModeMap.has(i.order_item_id)).map((i: any) => {
    const qty = selectedQty.get(i.order_item_id) ?? i.remaining_quantity;
    const isArma = isArmaItem(i.order_item_id);
    // Cada línea "Arma tu plato" usa su propio precio individual.
    const armaPrice = isArma ? getArmaPriceNum(i.order_item_id) : 0;
    // Para ítems normales usamos el precio por unidad CON recargos de
    // opciones (derivado del line_total persistido), no el precio base.
    const effectiveUnitPrice = isArma ? armaPrice : effUnitPrice(i);
    // BLINDAJE: redondeamos la línea a colones enteros para que cada
    // renglón impreso sea limpio y la suma de líneas == el monto
    // sellado en processPayment (sin "polvo" de punto flotante al
    // derivar effUnitPrice = line_total / quantity). Para precios
    // enteros (el 99% de los casos) el resultado es idéntico.
    const lineTotal = Math.round(effectiveUnitPrice * qty);
    return {
      ...i,
      is_arma_item: isArma,
      arma_price: isArma ? armaPrice : null,
      effective_unit_price: effectiveUnitPrice,
      pay_quantity: qty,
      pay_line_total: lineTotal
    };
  });
  const splitSelectedItems = unpaidItems.filter((i: any) => splitModeMap.has(i.order_item_id)).flatMap((i: any) => {
    const N = splitModeMap.get(i.order_item_id)!;
    const selectedParts = splitSelectedMap.get(i.order_item_id) || new Set();
    if (selectedParts.size === 0) return [];
    // Precio por unidad CON recargos de opciones (ver effUnitPrice).
    const unitPrice = effUnitPrice(i);
    const persisted = persistedSplits.get(i.order_item_id);
    const paidParts = persisted?.paidParts ?? new Set<number>();
    const paidAmounts = persisted?.paidAmounts ?? new Map<number, number>();
    const amounts = splitAmountsMap.get(i.order_item_id) ?? computeEqualSplit(unitPrice, N, paidParts, paidAmounts);
    return Array.from(selectedParts).map(partIndex => {
      const partPrice = Math.round(amounts[partIndex] ?? 0);
      return {
        ...i,
        is_arma_item: false,
        arma_price: null,
        effective_unit_price: unitPrice,
        pay_quantity: 1 / N,
        pay_line_total: partPrice,
        notes: `Parte ${partIndex + 1}/${N}`,
        is_split_part: true,
        split_part_index: partIndex
      };
    });
  });
  const selectedItems = [...normalSelectedItems, ...splitSelectedItems];
  const hasSelectedArmaItems = selectedItems.some((i: any) => i.is_arma_item);
  // Bloqueamos el cobro si CUALQUIER línea arma seleccionada no tiene
  // un precio individual válido (> 0).
  const hasIncompleteArmaPrices = selectedItems.some((i: any) => i.is_arma_item && (i.effective_unit_price || 0) <= 0);
  const total = selectedItems.reduce((s: number, i: any) => s + (i.pay_line_total || 0), 0);
  // Total final = subtotal de los ítems - descuento (clamp en 0 para
  // evitar negativos si el cajero pone un descuento mayor al total).
  const totalAfterDiscount = Math.max(0, total - (discountAmount || 0));
  // ─── Validación de división manual ──────────────────────────────
  // Para cada ítem en modo división, la suma de TODAS las partes
  // (pagadas + no pagadas) debe ser exactamente igual al total del
  // ítem, sin negativos y sin que una parte supere el total. Si algo
  // no cuadra, bloqueamos el cobro.
  const hasInvalidSplit = useMemo(() => {
    for (const [id, N] of splitModeMap.entries()) {
      const itm = unpaidItems.find((i: any) => i.order_item_id === id);
      if (!itm) continue;
      const unitPrice = Math.round(effUnitPrice(itm));
      const persisted = persistedSplits.get(id);
      const paidParts = persisted?.paidParts ?? new Set<number>();
      const paidAmounts = persisted?.paidAmounts ?? new Map<number, number>();
      const amounts = splitAmountsMap.get(id) ?? computeEqualSplit(unitPrice, N, paidParts, paidAmounts);
      let sum = 0;
      for (let k = 0; k < N; k++) {
        const v = Math.round(amounts[k] ?? 0);
        if (v < 0 || v > unitPrice) return true;
        sum += v;
      }
      if (sum !== unitPrice) return true;
    }
    return false;
  }, [splitModeMap, splitAmountsMap, persistedSplits, unpaidItems]);
  // isPartial = no todas las líneas están seleccionadas, O alguna línea
  // se está cobrando con quantity < remaining. En cualquier caso la
  // orden queda con saldo pendiente.
  const isPartial = useMemo(() => {
    const selectedQtyMap = new Map<string, number>();
    for (const item of selectedItems) {
      const prev = selectedQtyMap.get(item.order_item_id) || 0;
      selectedQtyMap.set(item.order_item_id, prev + (item.pay_quantity || 0));
    }
    for (const unpaid of unpaidItems) {
      const payingQty = selectedQtyMap.get(unpaid.order_item_id) || 0;
      // Usamos un pequeño epsilon (0.001) para tolerar errores de
      // redondeo en punto flotante al sumar fracciones (ej: 1/3 + 1/3 + 1/3).
      if (unpaid.remaining_quantity - payingQty > 0.001) {
        return true;
      }
    }
    return false;
  }, [selectedItems, unpaidItems]);
  const handleSubmit = async () => {
    if (!orderId || !restaurantId) {
      setError('Falta información de la mesa.');
      return;
    }
    if (selectedItems.length === 0) {
      setError('Selecciona al menos un ítem para cobrar.');
      return;
    }
    if (hasIncompleteArmaPrices) {
      // Abort submission if there are incomplete arma prices
      setError('Ingresa el precio del plato "Arma tu plato" antes de cobrar.');
      return;
    }
    if (hasInvalidSplit) {
      setError('La suma de las partes debe ser exactamente igual al total del ítem.');
      return;
    }
    // ─── NatuCafé (id 19): comprobante con datos del cliente ──────────
    // Si el cajero activó "Imprimir comprobante con datos del cliente"
    // pero NO escribió el nombre, NO permitimos cobrar — el comprobante
    // saldría en blanco. Solo aplica a NatuCafé; el resto de
    // restaurantes no se ve afectado.
    if (String(restaurantId) === '19' && showCustomerOnInvoice && !invoiceCustomerName.trim()) {
      setError('Escribí el nombre del cliente para imprimir el comprobante.');
      return;
    }
    setError(null);
    setSubmitting(true);
    // Persist arma item prices and update order totals.
    // Cada línea "Arma tu plato" tiene su propio precio individual:
    // persistimos el monto que el cajero asignó a cada una.
    try {
      const armaItemsToPersist = selectedItems.filter((ai: any) => ai.is_arma_item);
      for (const ai of armaItemsToPersist) {
        const unitPrice = ai.effective_unit_price ?? 0;
        await supabase.from('order_items').update({
          unit_price_snapshot: unitPrice,
          line_total: unitPrice * (ai.quantity || 1)
        }).eq('id', ai.order_item_id);
      }
      const {
        data: freshOrderItems
      } = await supabase.from('order_items').select('line_total, status').eq('order_id', orderId);
      const newSubtotal = (freshOrderItems || []).filter((oi: any) => oi.status !== 'cancelled').reduce((sum: number, oi: any) => sum + (Number(oi.line_total) || 0), 0);
      const {
        data: orderData
      } = await supabase.from('orders').select('delivery_fee').eq('id', orderId).single();
      const deliveryFee = orderData?.delivery_fee as number || 0;
      const newTotal = newSubtotal + deliveryFee;
      await supabase.from('orders').update({
        subtotal: newSubtotal,
        total: newTotal,
        updated_at: new Date().toISOString()
      }).eq('id', orderId);
    } catch (err) {
      console.warn('[NOIZA] error updating arma prices and totals:', err);
      // Non-blocking, do not throw
    }
    // Snapshot lleva pay_quantity (no la quantity original) y
    // pay_line_total como line_total. Así la factura/comprobante muestra
    // exactamente lo que se cobró.
    const snapshot: PaymentItemSnapshot[] = selectedItems.map((i: any) => ({
      order_item_id: i.order_item_id,
      item_name: i.item_name,
      quantity: i.pay_quantity,
      unit_price: i.effective_unit_price ?? i.unit_price,
      line_total: i.pay_line_total,
      notes: i.notes || null
    }));
    // ─── Descuento universal ──────────────────────────────────────
    // Se inyecta como una línea sintética negativa al final del
    // snapshot. El ID '__discount__' es inocuo en el loop de
    // paidQtyMap (no matchea ningún order_items.id real) y se
    // renderiza automáticamente en:
    //  · openPrintableInvoice → iterador de items_json
    //  · send-invoice-email   → iterador de items_json
    //  · NatuCafé bridge      → format_invoice_ticket_natucafe
    if ((discountAmount || 0) > 0) {
      snapshot.push({
        order_item_id: '__discount__',
        item_name: 'Descuento',
        quantity: 1,
        unit_price: -(discountAmount || 0),
        line_total: -(discountAmount || 0),
        notes: null
      } as PaymentItemSnapshot);
    }
    const result = await processPayment({
      restaurantId,
      orderId,
      tableId: table?.id || null,
      amount: totalAfterDiscount,
      method,
      items: snapshot,
      isPartial,
      createdBy: createdBy || null
    });
    setSubmitting(false);
    if (!result.success || !result.payment) {
      setError(result.error || 'Error al procesar el pago.');
      return;
    }
    await refetch();
    await refetchPaidQty();
    // ─── Persistir descuento en orders.discount_amount ────────────
    // Sumamos al valor previo por si en cobros parciales se asignaron
    // descuentos en distintos pagos. Si la columna no existe en el
    // schema (otros restaurantes), el error se loguea y seguimos:
    // el pago ya quedó registrado.
    if ((discountAmount || 0) > 0 && orderId) {
      try {
        const {
          data: ordRow
        } = await supabase.from('orders').select('discount_amount').eq('id', orderId).single();
        const prevDiscount = Number((ordRow as any)?.discount_amount) || 0;
        const {
          error: discErr
        } = await supabase.from('orders').update({
          discount_amount: prevDiscount + (discountAmount || 0)
        }).eq('id', orderId);
        if (discErr) {
          console.warn('[NOIZA][discount] update failed:', discErr);
        }
      } catch (e) {
        console.warn('[NOIZA][discount] update exception:', e);
      }
    }
    // ─── NatuCafé (id 19) — Auto-print de factura ──────────────────
    // Insertamos un job pending en order_print_jobs con
    // target_printer_type='invoice' para que el bridge dual lo mande
    // a la impresora de facturas configurada en
    // restaurant_printer_settings. SOLO NatuCafé — Rancho y demás
    // restaurantes mantienen su flujo intacto. Nunca bloqueamos al
    // cajero: si la inserción falla, loguemos y seguimos.
    // Flag de verificación dura del comprobante (solo NatuCafé invoice).
    let natucafeInvoiceVerifyFailed = false;
    if (String(restaurantId) === '19' && orderId) {
      try {
        const {
          data: orderRow
        } = await supabase.from('orders').select('tracking_token, order_number, customer_name, customer_phone, delivery_type, table_id, notes, subtotal, delivery_fee, total, source, created_at').eq('id', orderId).single();
        // ⚠️ FIX 6 (NatuCafé — cobro dividido / factura parcial 2026-06-05):
        // El payload de la factura impresa debe contener EXACTAMENTE los
        // ítems (y cantidades) que el cajero seleccionó para este pago —
        // NO toda la orden. Antes re-consultábamos todos los order_items
        // de la orden, así que en un split-bill el cliente que pagó su
        // parte recibía una factura con TODA la cuenta. Ahora derivamos
        // el payload del MISMO `selectedItems` que alimenta el snapshot
        // del pago (pay_quantity / pay_line_total / effective_unit_price),
        // de modo que la factura impresa coincide línea por línea y en
        // monto con lo seleccionado en Caja y con el comprobante web.
        //
        // Los ítems cancelados ya están excluidos por construcción: solo
        // los ítems pagables (unpaidItems) llegan a selectedItems.
        const selectedOrderItemIds = selectedItems.map((i: any) => i.order_item_id).filter((id: any) => id && id !== '__discount__');
        const optionsByItemId: Record<string, any[]> = {};
        if (selectedOrderItemIds.length > 0) {
          const {
            data: optRows
          } = await supabase.from('order_item_options').select('order_item_id, option_group, option_name, price_delta').in('order_item_id', selectedOrderItemIds);
          (optRows || []).forEach((o: any) => {
            if (!optionsByItemId[o.order_item_id]) optionsByItemId[o.order_item_id] = [];
            optionsByItemId[o.order_item_id].push({
              option_group: o.option_group,
              option_name: o.option_name,
              price_delta: o.price_delta
            });
          });
        }
        const itemsPayload = selectedItems.map((i: any) => ({
          menu_item_id: menuItemIdMap.get(String(i.order_item_id)) ?? null,
          item_name_snapshot: i.item_name,
          unit_price_snapshot: i.effective_unit_price ?? i.unit_price,
          quantity: i.pay_quantity,
          notes: i.notes || null,
          station_code: i.station_code ?? null,
          line_total: i.pay_line_total,
          selected_options: optionsByItemId[i.order_item_id] || []
        }));
        // Append descuento sintético al payload del ticket de factura
        // para que aparezca como línea negativa en el térmico.
        if ((discountAmount || 0) > 0) {
          itemsPayload.push({
            menu_item_id: null,
            item_name_snapshot: 'Descuento',
            unit_price_snapshot: -(discountAmount || 0),
            quantity: 1,
            notes: null,
            station_code: null,
            line_total: -(discountAmount || 0),
            selected_options: []
          });
        }
        // ─── FACTURA POR CADA PAGO (cobro dividido) ──────────────────
        // CONTRATO (NatuCafé id 19): este bloque corre en CADA pago que
        // registra el cajero — parcial o total. NO está condicionado a
        // que la mesa quede saldada. Por lo tanto:
        //   · Cada pago parcial genera SU PROPIO print job de factura.
        //   · `items` = SOLO las líneas/partes pagadas en ESTE pago
        //     (derivadas de `selectedItems`, con su pay_quantity /
        //     pay_line_total), nunca toda la orden.
        //   · `total` = EXACTAMENTE el monto de ESTE pago
        //     (`totalAfterDiscount`), que es el mismo `amount` que se
        //     pasó a processPayment → la factura impresa y el cobro
        //     coinciden al céntimo.
        //   · `created_at` = AHORA, para que cada factura parcial salga
        //     con la hora real de su pago (no la de creación de la orden).
        //   · `trigger` único por payment.id → el UNIQUE INDEX de dedupe
        //     (order_id, payload->>'trigger') NO bloquea los siguientes
        //     pagos: cada pago tiene su propio job.
        // Al finalizar la mesa NO se imprime ninguna factura adicional
        // por el total completo: este bloque es el ÚNICO creador de
        // facturas de NatuCafé y siempre factura por pago.
        //
        // ─── Datos del cliente del comprobante (estado DEDICADO) ─────
        // Se calculan JUSTO antes de armar el payload con los valores
        // ACTUALES de los inputs del comprobante. NO usamos customerName
        // (mesa / correo) para nada de la factura impresa.
        const cleanInvoiceCustomerName = showCustomerOnInvoice ? invoiceCustomerName.trim() : '';
        const cleanInvoiceCustomerId = showCustomerOnInvoice ? invoiceCustomerId.trim() : '';
        const invoicePayload = {
          trigger: `invoice_payment_${result.payment.id}`,
          payment_id: result.payment.id,
          // `is_partial` (interno) + `is_partial_invoice` (alias del
          // contrato pedido): ambos indican si este pago dejó saldo
          // pendiente. El bridge no los necesita para imprimir (usa
          // items + total), pero los dejamos para auditoría / reportes.
          is_partial: isPartial,
          is_partial_invoice: isPartial,
          payment_method: method,
          order_id: orderId,
          order_number: orderRow?.order_number,
          // Fallback seguro pedido para la factura NatuCafé: si el cajero
          // activó el checkbox y escribió nombre, ese nombre manda; si no,
          // cae al customer_name de la orden (ej. "Mesa 05"); nunca null.
          customer_name: cleanInvoiceCustomerName || orderRow?.customer_name || '',
          customer_phone: orderRow?.customer_phone,
          delivery_type: orderRow?.delivery_type,
          table_id: orderRow?.table_id,
          notes: orderRow?.notes,
          // Subtotal de la PARTE pagada (suma de los ítems seleccionados
          // antes del descuento), NO de la orden completa. delivery_fee
          // va en 0 porque el monto cobrado es solo de las líneas
          // seleccionadas (en dine-in no se cobra envío por persona).
          subtotal: total,
          delivery_fee: 0,
          total: totalAfterDiscount,
          // `paid_amount` = monto EXACTO cobrado en ESTE pago (== total
          // de la parte, == payments.amount). Es lo que se imprime en
          // la factura de este pago, nunca el total de la mesa.
          paid_amount: totalAfterDiscount,
          discount_amount: discountAmount || 0,
          source: orderRow?.source,
          created_at: new Date().toISOString(),
          items: itemsPayload,
          // ─── Datos del cliente en el comprobante impreso ───────────
          // Gated por el checkbox "Imprimir comprobante con datos del
          // cliente". El bridge SOLO imprime estos datos cuando
          // show_customer_on_invoice === true; si es false la factura
          // sale idéntica a hoy. invoice_customer_id se envía también
          // como customer_id_number por compatibilidad con el bridge.
          // Datos del cliente para el comprobante impreso. El bridge lee
          // estos campos con payload.get(...). Enviamos SIEMPRE strings
          // definidos (nunca null) para que el .get() del bridge reciba
          // un valor concreto. Regla: si el checkbox está activo y hay
          // texto, viaja el dato; si está apagado o vacío, viaja '' →
          // el bridge imprime "Cliente:" y "Cedula" en blanco igual que hoy.
          show_customer_on_invoice: showCustomerOnInvoice,
          invoice_customer_name: cleanInvoiceCustomerName,
          invoice_customer_id: cleanInvoiceCustomerId,
          // Alias de compatibilidad con el bridge (mismo valor, string).
          customer_id_number: cleanInvoiceCustomerId,
          // ─── DEBUG TEMPORAL — identificar el origen del print job ──
          // Si el último invoice job en order_print_jobs NO trae estos
          // campos, fue creado por otro lugar (trigger / edge / código
          // viejo), no por este componente. Quitar tras el diagnóstico.
          debug_source: 'PayTableModal',
          debug_version: 'invoice-customer-payload-v2',
          debug_created_at: new Date().toISOString()
        };
        const payloadFactura = invoicePayload;
        // ── LOG FINAL ANTES DEL INSERT ──────────────────────────────
        console.log('[NOIZA][NATUCAFE][INVOICE_PAYLOAD_FINAL]', payloadFactura);
        const {
          data: insertedJob,
          error: pjError
        } = await supabase.from('order_print_jobs').insert({
          restaurant_id: 19,
          order_id: orderRow?.tracking_token || null,
          order_number: orderRow?.order_number,
          status: 'pending',
          target_printer_type: 'invoice',
          payload: payloadFactura
        }).select('id, restaurant_id, target_printer_type, status, payload').maybeSingle();
        if (pjError) {
          console.warn('[NOIZA][NatuCafé][invoice-print] insert failed:', pjError);
        }
        // ── LOG DEL JOB REALMENTE INSERTADO ─────────────────────────
        console.log('[NOIZA][NATUCAFE][INVOICE_JOB_INSERTED]', insertedJob);
        // ── VERIFICACIÓN DURA ───────────────────────────────────────
        // Si el cajero pidió datos del cliente pero el job quedó
        // guardado SIN nombre, abortamos: NO marcamos el pago como
        // completado y mostramos el error. Solo NatuCafé + invoice.
        if (showCustomerOnInvoice && insertedJob && insertedJob.target_printer_type === 'invoice' && !String((insertedJob.payload as any)?.invoice_customer_name || '').trim()) {
          console.error('[NOIZA][NATUCAFE][INVOICE_JOB_INSERTED] invoice_customer_name vacío pese a showCustomerOnInvoice=true', insertedJob);
          natucafeInvoiceVerifyFailed = true;
        }
      } catch (printErr) {
        console.warn('[NOIZA][NatuCafé][invoice-print] exception:', printErr);
      }
    }
    // Si la verificación dura falló, NO avanzamos a "pagado": dejamos
    // al cajero corregir el nombre y reintentar la impresión.
    if (natucafeInvoiceVerifyFailed) {
      setSubmitting(false);
      setError('El comprobante no guardó el nombre del cliente. Verificá el nombre e intentá cobrar de nuevo.');
      return;
    }
    // allPaid = todas las líneas se cobraron completas en este pago Y
    // no quedan otras líneas pendientes.
    const allPaidNow = !isPartial;
    setCompletedPayment(result.payment);
    setAllPaid(allPaidNow);
    // ─── "Enviar más tarde": guardamos los datos del cliente en la
    // orden y marcamos wants_invoice=true para que aparezca en
    // "Otras Herramientas → Enviar Facturas". NO bloqueamos al cajero
    // si esto falla; el pago ya quedó registrado.
    if (wantsInvoice && sendLater) {
      try {
        // Guardamos los datos del cliente Y pre-linkeamos el payment.id
        // recién creado. invoice_sent_at queda en NULL (todavía no se
        // envió el correo). Al pre-linkear invoice_payment_id, la página
        // "Enviar Facturas" sabe que el pago YA existe y NO debe crear
        // uno nuevo — solo llamar a la edge function para mandar el correo.
        const {
          error: invErr
        } = await supabase.from('orders').update({
          wants_invoice: true,
          invoice_customer_name: customerName.trim() || null,
          invoice_legal_id: customerLegalId.trim() || null,
          invoice_email: customerEmail.trim() || null,
          invoice_payment_id: result.payment.id
        }).eq('id', orderId);
        if (invErr) {
          console.warn('[NOIZA][invoice] error guardando solicitud para más tarde:', invErr);
        }
      } catch (err) {
        console.warn('[NOIZA][invoice] exception send-later update:', err);
      }
      setStep('paid');
      onSuccess(allPaidNow);
      return;
    }
    // ─── Auto-envío de factura por correo (Resend vía edge function) ───
    // Si el cliente activó "Solicitar factura" + "Enviar ahora" y dejó
    // un correo, llamamos a send-invoice-email AHORA mismo, recién
    // creado el payment. Esto evita el click manual y garantiza que
    // Resend reciba la invocación. Si falla, el cajero todavía puede
    // reintentar manualmente desde el paso 'invoice'.
    const trimmedEmail = customerEmail.trim();
    if (wantsInvoice && !sendLater && trimmedEmail) {
      const paymentId = result.payment.id;
      console.log('[invoice] calling send-invoice-email');
      console.log('[invoice] payment_id', paymentId);
      setAutoInvoiceSending(true);
      try {
        const {
          data,
          error: invokeError
        } = await supabase.functions.invoke('send-invoice-email', {
          body: {
            payment_id: paymentId,
            customer_email: trimmedEmail,
            customer_name: customerName.trim() || null,
            customer_legal_id: customerLegalId.trim() || null
          }
        });
        console.log('[invoice] response', data);
        if (invokeError) {
          console.log('[invoice] error', invokeError);
          let serverError = invokeError.message || 'Error invocando la función';
          try {
            const ctx: any = (invokeError as any).context;
            if (ctx && typeof ctx.json === 'function') {
              const body = await ctx.json();
              serverError = body?.error || body?.message || serverError;
            } else if (ctx?.error) {
              serverError = ctx.error;
            }
          } catch {
            /* swallow */
          }
          setAutoInvoiceResult({
            ok: false,
            msg: `No se pudo enviar la factura por correo: ${serverError}. Podés reintentarlo manualmente.`,
            email: trimmedEmail
          });
          setAutoInvoiceSending(false);
          // Fallback: caer al paso 'invoice' para que el cajero reintente.
          setStep('invoice');
          onSuccess(allPaidNow);
          return;
        }
        if (data?.success) {
          setAutoInvoiceResult({
            ok: true,
            msg: `Factura enviada a ${trimmedEmail}.`,
            email: trimmedEmail
          });
          setAutoInvoiceSending(false);
          // Éxito: vamos directo al paso 'paid' con el banner de
          // confirmación. El cajero NO necesita hacer nada más.
          setStep('paid');
          onSuccess(allPaidNow);
          return;
        }
        // Respuesta inesperada de la edge function (sin error pero sin success)
        console.log('[invoice] error', data);
        setAutoInvoiceResult({
          ok: false,
          msg: `Respuesta inesperada del servidor. Podés reintentarlo manualmente.`,
          email: trimmedEmail
        });
        setAutoInvoiceSending(false);
        setStep('invoice');
        onSuccess(allPaidNow);
        return;
      } catch (err: any) {
        console.log('[invoice] error', err);
        setAutoInvoiceResult({
          ok: false,
          msg: `Error al enviar la factura: ${err?.message || 'desconocido'}. Podés reintentarlo manualmente.`,
          email: trimmedEmail
        });
        setAutoInvoiceSending(false);
        setStep('invoice');
        onSuccess(allPaidNow);
        return;
      }
    }
    setStep(wantsInvoice ? 'invoice' : 'paid');
    onSuccess(allPaidNow);
  };
  // ─── Rancho San Francisco — Imprimir comanda desde PaidStep ─────────
  // Movido desde el modal de la mesa: el botón ahora vive en la segunda
  // pantalla (pago confirmado) y reemplaza "Sí, generar factura" para
  // Rancho. Sólo aplica a restaurant_id 28 — cero impacto en el resto.
  const isRancho = String(restaurantId ?? '') === '28';
  const [printingComanda, setPrintingComanda] = useState(false);
  const [printComandaResult, setPrintComandaResult] = useState<{
    ok: boolean;
    msg: string;
  } | null>(null);
  const handlePrintComanda = useCallback(async () => {
    if (!isRancho) return;
    if (!orderId) {
      setPrintComandaResult({
        ok: false,
        msg: 'Orden sin identificador.'
      });
      return;
    }
    setPrintingComanda(true);
    setPrintComandaResult(null);
    try {
      // 1) Fetch items + options para enriquecer item_name_snapshot con
      //    las opciones seleccionadas y marcar los ítems de "Arma tu plato"
      //    con prefijo [Arma]. Mismo patrón que TableManagementPage.
      let enrichedItems: any[] = [];
      try {
        const {
          data: rows
        } = await supabase.from('order_items').select('*').eq('order_id', orderId);
        // ⚠️ FIX 5 (Rancho audit 2026-05-31): filtrar items cancelados
        // ANTES de armar el payload del print job. Cuando el cajero
        // imprime la comanda manual desde el paso "paid" y previamente
        // hubo modificaciones (Modificar/Cancelar), las filas con
        // status='cancelled' siguen en la DB como historial. Sin este
        // filtro, el bridge imprimiría líneas que el cajero ya quitó
        // → cocina prepara items fantasma → pérdida. Mismo patrón de
        // filtro que useTableOrderItems (Capa 1).
        const rawItems = (rows || []).filter((r: any) => !['cancelled', 'canceled', 'removed', 'voided', 'deleted'].includes(String(r.status || '').toLowerCase().trim()));
        if (rawItems.length > 0) {
          const itemIds = rawItems.map((it: any) => it.id);
          const {
            data: opts
          } = await supabase.from('order_item_options').select('*').in('order_item_id', itemIds);
          const byItem: Record<string, any[]> = {};
          (opts || []).forEach((o: any) => {
            const k = String(o.order_item_id);
            if (!byItem[k]) byItem[k] = [];
            byItem[k].push(o);
          });
          enrichedItems = rawItems.map((it: any) => {
            const itemOptions = byItem[String(it.id)] || [];
            const baseName = String(it.item_name_snapshot || '').trim();
            const optsSummary = itemOptions.map((o: any) => String(o.option_name_snapshot || o.option_name || '').trim()).filter((s: string) => s.length > 0).join(' · ');
            const isArma = it.menu_item_id == null;
            const armaPrefix = isArma ? '[Arma] ' : '';
            const enrichedName = optsSummary ? `${armaPrefix}${baseName} · ${optsSummary}` : `${armaPrefix}${baseName}`;
            return {
              ...it,
              options: itemOptions,
              is_arma_tu_plato: isArma,
              original_item_name: baseName,
              item_name_snapshot: enrichedName
            };
          });
        }
      } catch (e) {
        console.warn('[RANCHO_PRINT][paid] items/options fetch non-blocking:', e);
      }
      // Resolve tracking_token (UUID) — order_print_jobs.order_id es UUID.
      let orderRefUuid: string | null = null;
      try {
        const {
          data: row
        } = await supabase.from('orders').select('tracking_token').eq('id', orderId).maybeSingle();
        orderRefUuid = (row as any)?.tracking_token ?? null;
      } catch {
        /* swallow */
      }
      if (!orderRefUuid) orderRefUuid = orderId as string;
      const triggerValue = `manual_paid_${Date.now()}`;
      const order = table?.current_order as any;
      const {
        error
      } = await supabase.from('order_print_jobs').insert({
        restaurant_id: 28,
        order_id: orderRefUuid,
        order_number: order?.order_number ?? null,
        status: 'pending',
        payload: {
          order,
          order_internal_id: orderId,
          order_number: order?.order_number ?? null,
          created_at: order?.created_at ?? null,
          customer_name: table?.table_number ? `Mesa ${table.table_number}` : 'Cliente',
          customer_phone: null,
          delivery_type: 'dine_in',
          payment_method: order?.payment_method ?? 'cash',
          table_number: table?.table_number ?? null,
          subtotal: order?.subtotal ?? 0,
          delivery_fee: 0,
          total: order?.total ?? 0,
          notes: order?.notes ?? null,
          items: enrichedItems,
          trigger: triggerValue
        }
      });
      if (error) {
        console.error('[RANCHO_PRINT][paid] insert error:', error);
        setPrintComandaResult({
          ok: false,
          msg: `No se pudo imprimir: ${error.message || 'error desconocido'}`
        });
      } else {
        setPrintComandaResult({
          ok: true,
          msg: '🖨 Enviado a la impresora'
        });
      }
    } catch (e: any) {
      console.error('[RANCHO_PRINT][paid] exception:', e);
      setPrintComandaResult({
        ok: false,
        msg: e?.message || 'Error al enviar a la impresora'
      });
    } finally {
      setPrintingComanda(false);
    }
  }, [isRancho, orderId, table]);
  if (!isOpen || !table) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-id="element-1202">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl" data-id="element-1203">
        {step === 'select' && <SelectStep table={table} billingInfo={billingInfo} infoLoading={infoLoading} itemsLoading={itemsLoading} unpaidItems={unpaidItems} selectedIds={selectedIds} allSelected={allSelected} toggleAll={toggleAll} toggleItem={toggleItem}
      // Quantity-based partial pay
      selectedQty={selectedQty} setQtyForItem={setQtyForItem}
      // Split a single shareable item
      persistedSplits={persistedSplits} splitModeMap={splitModeMap} setSplitModeMap={setSplitModeMap} splitSelectedMap={splitSelectedMap} setSplitSelectedMap={setSplitSelectedMap} splitAmountsMap={splitAmountsMap} setPartAmount={setPartAmount} seedEqualSplit={seedEqualSplit} hasInvalidSplit={hasInvalidSplit} method={method} setMethod={setMethod} error={error} isPartial={isPartial} total={total} selectedItems={selectedItems} submitting={submitting} onClose={onClose} onSubmit={handleSubmit} wantsInvoice={wantsInvoice} setWantsInvoice={setWantsInvoice} sendLater={sendLater} setSendLater={setSendLater} customerName={customerName} setCustomerName={setCustomerName} customerLegalId={customerLegalId} setCustomerLegalId={setCustomerLegalId} customerEmail={customerEmail} setCustomerEmail={setCustomerEmail} showCustomerOnInvoice={showCustomerOnInvoice} setShowCustomerOnInvoice={setShowCustomerOnInvoice} invoiceCustomerName={invoiceCustomerName} setInvoiceCustomerName={setInvoiceCustomerName} invoiceCustomerId={invoiceCustomerId} setInvoiceCustomerId={setInvoiceCustomerId} armaPriceMap={armaPriceMap} setArmaPrice={setArmaPrice} getArmaPriceNum={getArmaPriceNum} hasIncompleteArmaPrices={hasIncompleteArmaPrices} isArmaItem={isArmaItem} discountAmount={discountAmount} setDiscountAmount={setDiscountAmount} showDiscountPanel={showDiscountPanel} setShowDiscountPanel={setShowDiscountPanel} totalAfterDiscount={totalAfterDiscount} data-id="element-1204" />}

        {step === 'paid' && completedPayment && <PaidStep payment={completedPayment} allPaid={allPaid} autoInvoiceResult={autoInvoiceResult} autoInvoiceSending={autoInvoiceSending} isRancho={isRancho} onPrintComanda={handlePrintComanda} printingComanda={printingComanda} printComandaResult={printComandaResult} onClose={onClose} onGoInvoice={() => setStep('invoice')} onChargeRemaining={() => {
        setStep('select');
        setCompletedPayment(null);
        setSelectedIds(new Set());
      }} data-id="element-1205" />}

        {step === 'invoice' && completedPayment && billingInfo && <InvoiceStep table={table} billingInfo={billingInfo} payment={completedPayment} customerName={customerName} setCustomerName={setCustomerName} customerLegalId={customerLegalId} setCustomerLegalId={setCustomerLegalId} customerEmail={customerEmail} setCustomerEmail={setCustomerEmail} onBack={() => setStep('paid')} onClose={onClose} data-id="element-1206" />}

        {step === 'invoice' && completedPayment && !billingInfo && <div className="p-8 text-center" data-id="element-1207">
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-4" data-id="element-1208">
              <AlertCircle className="w-7 h-7 text-amber-600" data-id="element-1209" />
            </div>
            <h3 className="text-lg font-black text-gray-900 mb-2" data-id="element-1210">
              Faltan datos de facturación
            </h3>
            <p className="text-sm text-gray-600 mb-6 max-w-sm mx-auto leading-relaxed" data-id="element-1211">
              El restaurante aún no tiene configurados sus datos fiscales (razón
              social, cédula, régimen tributario). Sin estos datos no podemos
              emitir comprobantes legales.
            </p>
            <p className="text-xs text-gray-500 mb-6" data-id="element-1212">
              El cobro ya quedó registrado. Para habilitar la emisión de
              facturas corré los scripts{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-1213">
                ADD_BILLING_LEGAL_NAME.sql
              </code>{' '}
              y{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-1214">
                SETUP_NATUCAFE_BILLING_INFO.sql
              </code>{' '}
              en Supabase.
            </p>
            <Button onClick={onClose} className="bg-gray-900 hover:bg-gray-800 text-white font-bold" data-id="element-1215">
              Entendido
            </Button>
          </div>}
      </div>
    </div>;
}
// ─── STEP 1: Select items + payment method ────────────────────────────────
function SelectStep(props: {
  table: TableWithOrder;
  billingInfo: BillingInfo | null;
  infoLoading: boolean;
  itemsLoading: boolean;
  unpaidItems: any[];
  selectedIds: Set<string>;
  allSelected: boolean;
  toggleAll: () => void;
  toggleItem: (id: string) => void;
  // Quantity-based partial pay
  selectedQty: Map<string, number>;
  setQtyForItem: (id: string, newQty: number) => void;
  // Split a single shareable item
  persistedSplits: Map<string, {
    N: number;
    paidParts: Set<number>;
    paidAmounts: Map<number, number>;
  }>;
  splitModeMap: Map<string, number>;
  setSplitModeMap: React.Dispatch<React.SetStateAction<Map<string, number>>>;
  splitSelectedMap: Map<string, Set<number>>;
  setSplitSelectedMap: React.Dispatch<React.SetStateAction<Map<string, Set<number>>>>;
  splitAmountsMap: Map<string, number[]>;
  setPartAmount: (id: string, k: number, rawValue: number) => void;
  seedEqualSplit: (id: string, N: number) => void;
  hasInvalidSplit: boolean;
  method: 'cash' | 'sinpe' | 'card';
  setMethod: (m: 'cash' | 'sinpe' | 'card') => void;
  error: string | null;
  isPartial: boolean;
  total: number;
  selectedItems: any[];
  submitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
  // Invoice (factura) — collected BEFORE payment
  wantsInvoice: boolean;
  setWantsInvoice: (b: boolean) => void;
  sendLater: boolean;
  setSendLater: (b: boolean) => void;
  customerName: string;
  setCustomerName: (s: string) => void;
  customerLegalId: string;
  setCustomerLegalId: (s: string) => void;
  customerEmail: string;
  setCustomerEmail: (s: string) => void;
  showCustomerOnInvoice: boolean;
  setShowCustomerOnInvoice: (b: boolean) => void;
  // NatuCafé — estado dedicado del comprobante impreso (NO reutiliza
  // customerName/customerLegalId del form de correo).
  invoiceCustomerName: string;
  setInvoiceCustomerName: (s: string) => void;
  invoiceCustomerId: string;
  setInvoiceCustomerId: (s: string) => void;
  // Rancho — "Arma tu plato": UN SOLO monto para todo el grupo
  armaPriceMap: Map<string, string>;
  setArmaPrice: (id: string, value: string) => void;
  getArmaPriceNum: (id: string) => number;
  hasIncompleteArmaPrices: boolean;
  isArmaItem: (id: string) => boolean;
  // Discount (universal)
  discountAmount: number;
  setDiscountAmount: React.Dispatch<React.SetStateAction<number>>;
  showDiscountPanel: boolean;
  setShowDiscountPanel: React.Dispatch<React.SetStateAction<boolean>>;
  totalAfterDiscount: number;
}) {
  const {
    table,
    itemsLoading,
    unpaidItems,
    selectedIds,
    allSelected,
    toggleAll,
    toggleItem,
    selectedQty,
    setQtyForItem,
    persistedSplits,
    splitModeMap,
    setSplitModeMap,
    splitSelectedMap,
    setSplitSelectedMap,
    splitAmountsMap,
    setPartAmount,
    seedEqualSplit,
    hasInvalidSplit,
    method,
    setMethod,
    error,
    isPartial,
    total,
    selectedItems,
    submitting,
    onClose,
    onSubmit,
    wantsInvoice,
    setWantsInvoice,
    sendLater,
    setSendLater,
    customerName,
    setCustomerName,
    customerLegalId,
    setCustomerLegalId,
    customerEmail,
    setCustomerEmail,
    showCustomerOnInvoice,
    setShowCustomerOnInvoice,
    invoiceCustomerName,
    setInvoiceCustomerName,
    invoiceCustomerId,
    setInvoiceCustomerId,
    armaPriceMap,
    setArmaPrice,
    getArmaPriceNum,
    hasIncompleteArmaPrices,
    isArmaItem,
    discountAmount,
    setDiscountAmount,
    showDiscountPanel,
    setShowDiscountPanel,
    totalAfterDiscount
  } = props;
  // ─── BLINDAJE (local a SelectStep): precio por unidad CON recargos ──
  // SelectStep es un componente separado de PayTableModal, así que NO
  // tiene acceso al effUnitPrice del padre. Definimos una copia local
  // pura (sin dependencias) para incluir el recargo de opciones de
  // Rancho (casado con costilla, patacones extra) en lo que se muestra.
  // Misma lógica: line_total/quantity, con fallback a unit_price.
  const effUnitPrice = (i: any): number => {
    const qty = Number(i?.quantity) || 0;
    const lt = Number(i?.line_total);
    if (qty > 0 && Number.isFinite(lt) && lt > 0) {
      return lt / qty;
    }
    return Number(i?.unit_price) || 0;
  };
  // ─── Cash received → change calculator (only shown when method = cash) ───
  const [cashReceived, setCashReceived] = useState<string>('');
  // Reset cash field whenever method changes or total changes meaningfully
  useEffect(() => {
    if (method !== 'cash') setCashReceived('');
  }, [method]);
  const cashReceivedNum = parseFloat(cashReceived.replace(/[^0-9.]/g, '')) || 0;
  const change = cashReceivedNum - totalAfterDiscount;
  const hasEnoughCash = cashReceivedNum >= totalAfterDiscount && cashReceivedNum > 0;
  const quickCashAmounts = useMemo(() => {
    if (totalAfterDiscount <= 0) return [] as number[];
    // Round up to nearest 1 000, 5 000, 10 000, 20 000 — only keep those >= total
    const candidates = [Math.ceil(totalAfterDiscount / 1000) * 1000, Math.ceil(totalAfterDiscount / 5000) * 5000, Math.ceil(totalAfterDiscount / 10000) * 10000, Math.ceil(totalAfterDiscount / 20000) * 20000];
    return Array.from(new Set(candidates)).filter(v => v >= totalAfterDiscount).slice(0, 4);
  }, [totalAfterDiscount]);
  // ─── Discount panel handlers ──────────────────────────────────
  const [otherDiscountInput, setOtherDiscountInput] = useState<string>('');
  const presetDiscounts = [1000, 2000, 3000, 4000, 5000];
  const applyPresetDiscount = (amt: number) => {
    setDiscountAmount(amt);
    setOtherDiscountInput('');
  };
  const applyOtherDiscount = () => {
    const v = Number(otherDiscountInput.replace(/[^0-9.]/g, '')) || 0;
    setDiscountAmount(v);
  };
  const clearDiscount = () => {
    setDiscountAmount(0);
    setOtherDiscountInput('');
  };
  return <>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-1216">
        <div data-id="element-1217">
          <h2 className="text-xl font-black text-gray-900 flex items-center gap-2" data-id="element-1218">
            <Receipt className="w-5 h-5 text-amber-500" data-id="element-1219" />
            Cobrar{' '}
            {table.table_name ? table.table_name : table.table_number && !String(table.table_number).startsWith('order:') ? `Mesa ${table.table_number}` : `Pedido #${String((table.current_order as any)?.order_number || '').split('-').pop() || ''}`}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-1220">
            Selecciona los ítems a cobrar (división de cuenta disponible)
          </p>
        </div>
        <button onClick={onClose} disabled={submitting} className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" data-id="element-1221">
          <X className="w-5 h-5 text-gray-500" data-id="element-1222" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-id="element-1223">
        {itemsLoading ? <div className="flex justify-center py-12" data-id="element-1224">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" data-id="element-1225" />
          </div> : unpaidItems.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-1226">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400 mb-3" data-id="element-1227" />
            <p className="font-bold" data-id="element-1228">No hay ítems pendientes de cobro</p>
            <p className="text-sm" data-id="element-1229">Esta mesa ya fue cobrada por completo.</p>
          </div> : <>
            <button onClick={toggleAll} className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors" data-id="element-1230">
              <span className="flex items-center gap-2 text-sm font-bold text-gray-900" data-id="element-1231">
                {allSelected ? <CheckSquare className="w-4 h-4 text-amber-500" data-id="element-1232" /> : <Square className="w-4 h-4 text-gray-400" data-id="element-1233" />}
                {allSelected ? 'Deseleccionar todo' : 'Seleccionar todo'}
              </span>
              <span className="text-xs font-medium text-gray-500" data-id="element-1234">
                {selectedIds.size} / {unpaidItems.length} ítems
              </span>
            </button>

            {/* ── Arma tu plato — PRECIO INDIVIDUAL por línea ──
                Cada línea arma (menu_item_id == null) es un ítem
                independiente: checkbox propio + input de precio propio.
                El cajero elige cuáles cobrar y cuánto vale cada uno.
                Los platillos normales NO aparecen acá. */}
            {(() => {
          const armaItems = unpaidItems.filter((i: any) => isArmaItem(i.order_item_id));
          if (armaItems.length === 0) return null;
          const selectedArmaCount = armaItems.filter((i: any) => selectedIds.has(i.order_item_id)).length;
          return <div className="rounded-xl border-2 border-amber-200 bg-amber-50/30 px-3 py-3" data-id="element-1235">
                  <div className="flex items-center gap-2 mb-2" data-id="element-1236">
                    <span className="text-[10px] font-black uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded" data-id="element-1237">
                      Arma tu plato
                    </span>
                    <span className="text-[11px] font-bold text-gray-600" data-id="element-1238">
                      {selectedArmaCount} / {armaItems.length}{' '}
                      {armaItems.length === 1 ? 'línea' : 'líneas'}
                    </span>
                  </div>
                  <ul className="space-y-1.5 pl-0" data-id="element-1239">
                    {armaItems.map((ai: any) => {
                const isSelected = selectedIds.has(ai.order_item_id);
                const priceStr = armaPriceMap.get(ai.order_item_id) ?? '';
                const priceNum = getArmaPriceNum(ai.order_item_id);
                const needsPrice = isSelected && priceNum <= 0;
                return <li key={ai.order_item_id} className={cn('rounded-lg border px-2.5 py-2 transition-colors', needsPrice ? 'border-red-300 bg-red-50/40' : isSelected ? 'border-emerald-300 bg-emerald-50/40' : 'border-transparent bg-white/50')} data-id="element-1240">
                          <div className="flex items-center gap-2" data-id="element-1241">
                            <button type="button" onClick={() => toggleItem(ai.order_item_id)} className="flex items-center gap-2 min-w-0 flex-1 text-left" aria-label={isSelected ? 'Quitar de la cuenta' : 'Agregar a la cuenta'} data-id="element-1242">
                              {isSelected ? <CheckSquare className="w-4 h-4 text-amber-500 shrink-0" data-id="element-1243" /> : <Square className="w-4 h-4 text-gray-300 shrink-0" data-id="element-1244" />}
                              <span className={cn('text-sm font-bold truncate', isSelected ? 'text-gray-900' : 'text-gray-500')} data-id="element-1245">
                                {ai.item_name}
                              </span>
                            </button>
                            <div className="relative shrink-0" data-id="element-1246">
                              <ArmaPriceInput value={priceStr} onChange={val => setArmaPrice(ai.order_item_id, val)} needsPrice={needsPrice} itemName={ai.item_name} data-id="element-1247" />
                            </div>
                          </div>
                          {needsPrice && <p className="text-[10px] text-red-600 font-bold mt-1 pl-6" data-id="element-1248">
                              Ingresa el monto antes de cobrar.
                            </p>}
                        </li>;
              })}
                  </ul>
                </div>;
        })()}

            <div className="space-y-1.5" data-id="element-1249">
              {unpaidItems.filter((item: any) => !isArmaItem(item.order_item_id)).map((item: any) => {
            const isSplitMode = splitModeMap.has(item.order_item_id);
            const N = splitModeMap.get(item.order_item_id) || 2;
            const selectedParts = splitSelectedMap.get(item.order_item_id) || new Set();
            const persistedSplit = persistedSplits.get(item.order_item_id);
            const hasPaidParts = persistedSplit && persistedSplit.paidParts.size > 0;
            if (isSplitMode) {
              // effUnitPrice incluye el recargo de opciones (Rancho).
              const unitPrice = Math.round(effUnitPrice(item));
              const paidParts = persistedSplit?.paidParts ?? new Set<number>();
              const paidAmounts = persistedSplit?.paidAmounts ?? new Map<number, number>();
              const amounts = splitAmountsMap.get(item.order_item_id) ?? computeEqualSplit(unitPrice, N, paidParts, paidAmounts);
              const partsSum = amounts.slice(0, N).reduce((s, v) => s + Math.round(v || 0), 0);
              const sumOk = partsSum === unitPrice;
              return <div key={item.order_item_id} className="w-full flex flex-col gap-2 px-3 py-3 rounded-xl border-2 border-amber-400 bg-amber-50/30 transition-all" data-id="element-1250">
                        <div className="flex items-start justify-between" data-id="element-1251">
                          <div data-id="element-1252">
                            <p className="font-bold text-gray-900 text-sm truncate" data-id="element-1253">
                              {item.item_name}
                            </p>
                            <div className="flex items-center gap-2 mt-1" data-id="element-1254">
                              <span className="text-[11px] text-gray-500" data-id="element-1255">
                                Dividir en:
                              </span>
                              <select value={N} disabled={hasPaidParts} onChange={e => {
                        const newN = parseInt(e.target.value, 10);
                        const newSplitModeMap = new Map(splitModeMap);
                        newSplitModeMap.set(item.order_item_id, newN);
                        setSplitModeMap(newSplitModeMap);
                        const newSplitSelectedMap = new Map(splitSelectedMap);
                        const allParts = new Set(Array.from({
                          length: newN
                        }, (_, i) => i));
                        newSplitSelectedMap.set(item.order_item_id, allParts);
                        setSplitSelectedMap(newSplitSelectedMap);
                        // Recalcular montos en división equitativa
                        // al cambiar la cantidad de partes.
                        seedEqualSplit(item.order_item_id, newN);
                      }} className="text-xs border border-amber-300 rounded px-1 py-0.5 bg-white text-amber-900 focus:outline-none focus:ring-1 focus:ring-amber-500 disabled:opacity-50 disabled:bg-transparent disabled:border-transparent disabled:pl-0" data-id="element-1256">
                                {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => <option key={n} value={n} data-id="element-1257">
                                      {n} partes
                                    </option>)}
                              </select>
                              <span className="text-[11px] text-gray-500" data-id="element-1258">
                                · Total: {formatCurrency(unitPrice)}
                              </span>
                            </div>
                          </div>
                          {!hasPaidParts && <button type="button" onClick={() => {
                    const newSplitModeMap = new Map(splitModeMap);
                    newSplitModeMap.delete(item.order_item_id);
                    setSplitModeMap(newSplitModeMap);
                    const newSplitSelectedMap = new Map(splitSelectedMap);
                    newSplitSelectedMap.delete(item.order_item_id);
                    setSplitSelectedMap(newSplitSelectedMap);
                  }} className="text-xs font-bold text-amber-700 bg-amber-100 hover:bg-amber-200 px-2 py-1 rounded transition-colors" data-id="element-1259">
                              Cancelar división
                            </button>}
                        </div>
                        <div className="space-y-1 mt-1" data-id="element-1260">
                          {Array.from({
                    length: N
                  }).map((_, k) => {
                    const isPaid = paidParts.has(k);
                    const isSelected = selectedParts.has(k);
                    const partAmount = Math.round(amounts[k] ?? 0);
                    return <div key={k} className={cn('w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border transition-colors', isPaid ? 'border-transparent bg-gray-100 opacity-70' : isSelected ? 'border-amber-300 bg-white' : 'border-transparent hover:bg-white/50')} data-id="element-1261">
                                <button type="button" disabled={isPaid} onClick={() => {
                        if (isPaid) return;
                        const newSelectedParts = new Set(selectedParts);
                        if (isSelected) {
                          newSelectedParts.delete(k);
                        } else {
                          newSelectedParts.add(k);
                        }
                        const newSplitSelectedMap = new Map(splitSelectedMap);
                        newSplitSelectedMap.set(item.order_item_id, newSelectedParts);
                        setSplitSelectedMap(newSplitSelectedMap);
                      }} className={cn('flex items-center gap-2 text-left min-w-0', isPaid && 'cursor-not-allowed')} data-id="element-1262">
                                  {isPaid ? <CheckSquare className="w-4 h-4 shrink-0 text-gray-400" data-id="element-1263" /> : isSelected ? <CheckSquare className="w-4 h-4 shrink-0 text-amber-500" data-id="element-1264" /> : <Square className="w-4 h-4 shrink-0 text-gray-300" data-id="element-1265" />}
                                  <span className={cn('text-sm font-bold whitespace-nowrap', isPaid ? 'text-gray-500' : isSelected ? 'text-gray-900' : 'text-gray-500')} data-id="element-1266">
                                    Parte {k + 1}/{N}{' '}
                                    {isPaid && <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600" data-id="element-1267">
                                        (Pagada)
                                      </span>}
                                  </span>
                                </button>
                                {isPaid ? <span className="font-bold text-sm tabular-nums text-gray-500 shrink-0" data-id="element-1268">
                                    {formatCurrency(partAmount)}
                                  </span> : <div className="relative shrink-0" data-id="element-1269">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400 pointer-events-none" data-id="element-1270">
                                      ₡
                                    </span>
                                    <input type="text" inputMode="numeric" value={partAmount} onChange={e => {
                          const raw = Number(e.target.value.replace(/[^0-9]/g, ''));
                          setPartAmount(item.order_item_id, k, raw);
                        }} onFocus={e => e.target.select()} className="w-24 pl-5 pr-2 py-1 rounded-md border-2 border-amber-300 bg-white text-sm font-black tabular-nums text-right text-gray-900 focus:outline-none focus:border-amber-500" aria-label={`Monto parte ${k + 1}`} data-id="element-1271" />
                                  </div>}
                              </div>;
                  })}
                        </div>
                        {!sumOk && <div className="mt-1 flex items-center gap-1.5 rounded-lg bg-red-50 border border-red-200 px-2.5 py-1.5" data-id="element-1272">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0 text-red-600" data-id="element-1273" />
                            <p className="text-[11px] font-bold text-red-700 leading-snug" data-id="element-1274">
                              La suma de las partes ({formatCurrency(partsSum)})
                              debe ser igual al total (
                              {formatCurrency(unitPrice)}
                              ).
                            </p>
                          </div>}
                      </div>;
            }
            const isSelected = selectedIds.has(item.order_item_id);
            const remaining = item.remaining_quantity ?? item.quantity;
            const paidSoFar = item.paid_quantity ?? 0;
            const qty = selectedQty.get(item.order_item_id) ?? remaining;
            const lineTotal = effUnitPrice(item) * qty;
            const canDecrement = isSelected && qty > 1;
            const canIncrement = isSelected && qty < remaining;
            const canSplit = item.is_shareable && (item.unit_price || 0) > 0 && remaining >= 1;
            return <div key={item.order_item_id} className={cn('w-full flex items-start gap-3 px-3 py-3 rounded-xl border-2 transition-all', isSelected ? 'border-amber-400 bg-amber-50/50' : 'border-gray-100 hover:border-gray-200 bg-white')} data-id="element-1275">
                      <button type="button" onClick={() => toggleItem(item.order_item_id)} className="mt-0.5 shrink-0" aria-label={isSelected ? 'Quitar de la cuenta' : 'Agregar a la cuenta'} data-id="element-1276">
                        {isSelected ? <CheckSquare className="w-5 h-5 text-amber-500" data-id="element-1277" /> : <Square className="w-5 h-5 text-gray-300" data-id="element-1278" />}
                      </button>
                      <div className="flex-1 min-w-0" data-id="element-1279">
                        <button type="button" onClick={() => toggleItem(item.order_item_id)} className="w-full text-left" data-id="element-1280">
                          <div className="flex items-baseline gap-2 flex-wrap" data-id="element-1281">
                            <p className="font-bold text-gray-900 text-sm truncate" data-id="element-1282">
                              {item.item_name}
                            </p>
                            {paidSoFar > 0 && <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded" data-id="element-1283">
                                {paidSoFar} ya cobrado
                              </span>}
                          </div>
                          <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-1284">
                            {formatCurrency(Math.round(effUnitPrice(item)))} c/u
                            · quedan {remaining} de {item.quantity}
                          </p>
                          {Array.isArray((item as any).selected_options) && (item as any).selected_options.length > 0 && <div className="mt-0.5 space-y-0.5" data-id="element-1285">
                                {(item as any).selected_options.map((opt: any, idx: number) => <p key={idx} className="text-xs text-amber-700" data-id="element-1286">
                                      · {opt.option_name}
                                    </p>)}
                              </div>}
                          {item.notes && <p className="text-xs text-gray-500 italic mt-0.5" data-id="element-1287">
                              {item.notes}
                            </p>}
                        </button>
                        {canSplit && <div className="mt-2 flex items-center gap-2" data-id="element-1288">
                            <button type="button" onClick={() => {
                    const newSplitModeMap = new Map(splitModeMap);
                    newSplitModeMap.set(item.order_item_id, 2);
                    setSplitModeMap(newSplitModeMap);
                    const newSplitSelectedMap = new Map(splitSelectedMap);
                    newSplitSelectedMap.set(item.order_item_id, new Set([0, 1]));
                    setSplitSelectedMap(newSplitSelectedMap);
                    // Sembramos los montos en división equitativa.
                    seedEqualSplit(item.order_item_id, 2);
                  }} className="text-[11px] font-bold text-amber-700 bg-amber-100 hover:bg-amber-200 px-2 py-1 rounded transition-colors" data-id="element-1289">
                              Dividir este ítem
                            </button>
                          </div>}
                      </div>
                      <div className="flex flex-col items-end gap-1.5 shrink-0" data-id="element-1290">
                        {isSelected && remaining > 1 ? <div className="flex items-center gap-0 bg-white border-2 border-amber-300 rounded-lg overflow-hidden" data-id="element-1291">
                            <button type="button" onClick={() => setQtyForItem(item.order_item_id, qty - 1)} disabled={!canDecrement} className="w-7 h-7 flex items-center justify-center text-amber-600 hover:bg-amber-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" aria-label="Reducir cantidad" data-id="element-1292">
                              <Minus className="w-3.5 h-3.5" data-id="element-1293" />
                            </button>
                            <span className="w-8 text-center text-sm font-black text-gray-900 tabular-nums" data-id="element-1294">
                              {qty}
                            </span>
                            <button type="button" onClick={() => setQtyForItem(item.order_item_id, qty + 1)} disabled={!canIncrement} className="w-7 h-7 flex items-center justify-center text-amber-600 hover:bg-amber-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" aria-label="Aumentar cantidad" data-id="element-1295">
                              <Plus className="w-3.5 h-3.5" data-id="element-1296" />
                            </button>
                          </div> : <span className="text-xs font-bold text-gray-500 tabular-nums" data-id="element-1297">
                            {isSelected ? `${qty}×` : `${remaining}×`}
                          </span>}
                        <span className="font-bold text-gray-900 text-sm tabular-nums" data-id="element-1298">
                          {formatCurrency(isSelected ? selectedItems.find(si => si.order_item_id === item.order_item_id)?.pay_line_total ?? 0 : Math.round(effUnitPrice(item) * remaining))}
                        </span>
                      </div>
                    </div>;
          })}
            </div>

            <div className="pt-2" data-id="element-1299">
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-1300">
                Método de pago
              </p>
              <div className="grid grid-cols-3 gap-2" data-id="element-1301">
                {([{
              id: 'cash',
              label: 'Efectivo',
              Icon: Banknote
            }, {
              id: 'sinpe',
              label: 'SINPE',
              Icon: Smartphone
            }, {
              id: 'card',
              label: 'Tarjeta',
              Icon: CreditCard
            }] as const).map(({
              id,
              label,
              Icon
            }) => <button key={id} onClick={() => setMethod(id)} className={cn('flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all', method === id ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-1302">
                    <Icon className="w-5 h-5 mb-1" data-id="element-1303" />
                    <span className="text-xs font-bold" data-id="element-1304">{label}</span>
                  </button>)}
              </div>
            </div>

            {/* ─── Solicitar factura (Régimen Simplificado CR) ─── */}
            <div className={cn('rounded-2xl border-2 transition-colors', wantsInvoice ? 'border-gray-900 bg-gray-50' : 'border-gray-100 bg-white')} data-id="element-1305">
              <button type="button" onClick={() => setWantsInvoice(!wantsInvoice)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-id="element-1306">
                <div className="flex items-center gap-3" data-id="element-1307">
                  <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center transition-colors', wantsInvoice ? 'bg-gray-900 text-white' : 'bg-amber-50 text-amber-600')} data-id="element-1308">
                    <FileText className="w-5 h-5" data-id="element-1309" />
                  </div>
                  <div data-id="element-1310">
                    <p className="font-bold text-gray-900 text-sm" data-id="element-1311">
                      Enviar factura por correo electrónico
                    </p>
                    <p className="text-[11px] text-gray-500" data-id="element-1312">
                      Se enviará por correo electrónico (Régimen Simplificado)
                    </p>
                  </div>
                </div>
                <div className={cn('relative w-11 h-6 rounded-full transition-colors shrink-0', wantsInvoice ? 'bg-gray-900' : 'bg-gray-200')} data-id="element-1313">
                  <div className={cn('absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all', wantsInvoice ? 'left-[22px]' : 'left-0.5')} data-id="element-1314" />
                </div>
              </button>

              {wantsInvoice && <div className="px-4 pb-4 space-y-3 border-t border-gray-200 pt-3" data-id="element-1315">
                  {/* ─── ¿Cuándo enviar? ─── */}
                  <div data-id="element-1316">
                    <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-1317">
                      ¿Cuándo enviar la factura?
                    </label>
                    <div className="grid grid-cols-2 gap-2" data-id="element-1318">
                      <button type="button" onClick={() => setSendLater(false)} className={cn('flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 text-xs font-bold transition-all', !sendLater ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300')} data-id="element-1319">
                        <Mail className="w-3.5 h-3.5" data-id="element-1320" />
                        Sí, enviar ahora
                      </button>
                      <button type="button" onClick={() => setSendLater(true)} className={cn('flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 text-xs font-bold transition-all', sendLater ? 'border-amber-500 bg-amber-500 text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300')} data-id="element-1321">
                        <Clock className="w-3.5 h-3.5" data-id="element-1322" />
                        Sí, enviar más tarde
                      </button>
                    </div>
                    {sendLater && <p className="text-[10px] text-amber-700 mt-1.5 leading-snug" data-id="element-1323">
                        La solicitud quedará pendiente en{' '}
                        <span className="font-bold" data-id="element-1324">
                          Otras Herramientas → Enviar Facturas
                        </span>
                        .
                      </p>}
                  </div>
                  <div data-id="element-1325">
                    <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1" data-id="element-1326">
                      A nombre de
                    </label>
                    <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Nombre completo del cliente" className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-gray-900 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1327" />
                  </div>
                  {/* Cédula y correo solo se piden cuando se envía AHORA.
                   Si es "más tarde" basta con el nombre — el resto se
                   llena después desde Enviar Facturas. */}
                  {!sendLater && <>
                      <div data-id="element-1328">
                        <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1" data-id="element-1329">
                          Cédula / Identificación
                          <span className="text-gray-400 font-medium normal-case ml-1" data-id="element-1330">
                            (opcional)
                          </span>
                        </label>
                        <input type="text" value={customerLegalId} onChange={e => setCustomerLegalId(e.target.value)} placeholder="Ej. 1-1234-5678" className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-gray-900 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1331" />
                      </div>
                      <div data-id="element-1332">
                        <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1" data-id="element-1333">
                          Correo electrónico
                        </label>
                        <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="cliente@correo.com" className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-gray-900 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1334" />
                        <p className="text-[10px] text-gray-500 mt-1" data-id="element-1335">
                          Puedes dejar los datos en blanco y completarlos en el
                          siguiente paso si el cliente aún no los tiene.
                        </p>
                      </div>
                    </>}
                  {sendLater && <p className="text-[10px] text-gray-500 leading-snug" data-id="element-1336">
                      Solo necesitamos el nombre para registrar la solicitud.
                      Cédula y correo se completan después desde{' '}
                      <span className="font-bold" data-id="element-1337">Enviar Facturas</span>.
                    </p>}
                </div>}
            </div>

            {/* ─── Imprimir comprobante con datos del cliente ─── */}
            {/* Independiente del envío por correo. Controla si el ticket
                impreso por el bridge muestra nombre + cédula del cliente.
                Si está apagado, la factura impresa sale igual que hoy. */}
            <div className={cn('rounded-2xl border-2 transition-colors', showCustomerOnInvoice ? 'border-cyan-300 bg-cyan-50/40' : 'border-gray-100 bg-white')} data-id="element-1338">
              <label className="w-full flex items-center justify-between px-4 py-3 cursor-pointer" data-id="element-1339">
                <div className="flex items-center gap-3" data-id="element-1340">
                  <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center transition-colors', showCustomerOnInvoice ? 'bg-cyan-500 text-white' : 'bg-cyan-50 text-cyan-600')} data-id="element-1341">
                    <Receipt className="w-5 h-5" data-id="element-1342" />
                  </div>
                  <div data-id="element-1343">
                    <p className="font-bold text-gray-900 text-sm" data-id="element-1344">
                      Imprimir comprobante con datos del cliente
                    </p>
                    <p className="text-[11px] text-gray-500" data-id="element-1345">
                      Agrega nombre y cédula al comprobante impreso.
                    </p>
                  </div>
                </div>
                <input type="checkbox" checked={showCustomerOnInvoice} onChange={e => setShowCustomerOnInvoice(e.target.checked)} className="w-5 h-5 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500 cursor-pointer" data-id="element-1346" />
              </label>
              {showCustomerOnInvoice && <div className="px-4 pb-4 space-y-3 border-t border-cyan-100 pt-3" data-id="element-1347">
                  <div data-id="element-1348">
                    <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1" data-id="element-1349">
                      Nombre del cliente
                    </label>
                    <input type="text" value={invoiceCustomerName} onChange={e => setInvoiceCustomerName(e.target.value)} placeholder="Nombre completo del cliente" className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-gray-900 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1350" />
                  </div>
                  <div data-id="element-1351">
                    <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1" data-id="element-1352">
                      Cédula / Identificación
                      <span className="text-gray-400 font-medium normal-case ml-1" data-id="element-1353">
                        (opcional)
                      </span>
                    </label>
                    <input type="text" value={invoiceCustomerId} onChange={e => setInvoiceCustomerId(e.target.value)} placeholder="Ej. 1-1234-5678" className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-gray-900 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1354" />
                  </div>
                </div>}
            </div>

            {/* ─── Asignar descuento (universal, todos los KDS) ─── */}
            <div className={cn('rounded-2xl border-2 transition-colors', showDiscountPanel || discountAmount > 0 ? 'border-emerald-300 bg-emerald-50/40' : 'border-gray-100 bg-white')} data-id="element-1355">
              <button type="button" onClick={() => setShowDiscountPanel(!showDiscountPanel)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-id="element-1356">
                <div className="flex items-center gap-3" data-id="element-1357">
                  <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center transition-colors', discountAmount > 0 ? 'bg-emerald-500 text-white' : 'bg-emerald-50 text-emerald-600')} data-id="element-1358">
                    <span className="text-lg font-black" data-id="element-1359">%</span>
                  </div>
                  <div data-id="element-1360">
                    <p className="font-bold text-gray-900 text-sm" data-id="element-1361">
                      Asignar descuento
                    </p>
                    <p className="text-[11px] text-gray-500" data-id="element-1362">
                      {discountAmount > 0 ? `Aplicado: -${formatCurrency(discountAmount)}` : 'Resta un monto al total a cobrar'}
                    </p>
                  </div>
                </div>
                <div className={cn('text-xs font-black uppercase tracking-wider', showDiscountPanel ? 'text-emerald-700' : 'text-gray-400')} data-id="element-1363">
                  {showDiscountPanel ? 'Cerrar' : 'Abrir'}
                </div>
              </button>

              {showDiscountPanel && <div className="px-4 pb-4 space-y-3 border-t border-emerald-200 pt-3" data-id="element-1364">
                  <div data-id="element-1365">
                    <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-2" data-id="element-1366">
                      Montos rápidos
                    </label>
                    <div className="grid grid-cols-5 gap-2" data-id="element-1367">
                      {presetDiscounts.map(amt => {
                  const active = discountAmount === amt;
                  return <button key={amt} type="button" onClick={() => applyPresetDiscount(amt)} className={cn('px-2 py-2 rounded-lg border-2 text-xs font-black transition-colors tabular-nums', active ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-emerald-200 bg-white text-emerald-700 hover:border-emerald-400')} data-id="element-1368">
                            ₡{amt.toLocaleString('es-CR')}
                          </button>;
                })}
                    </div>
                  </div>
                  <div data-id="element-1369">
                    <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-1370">
                      Otro monto
                    </label>
                    <div className="flex gap-2" data-id="element-1371">
                      <div className="relative flex-1" data-id="element-1372">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base font-black text-emerald-600 pointer-events-none" data-id="element-1373">
                          ₡
                        </span>
                        <input type="text" inputMode="decimal" value={otherDiscountInput} onChange={e => setOtherDiscountInput(e.target.value.replace(/[^0-9.]/g, ''))} onBlur={applyOtherDiscount} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyOtherDiscount();
                    }
                  }} placeholder="Ingrese el monto" className="w-full pl-8 pr-3 py-2 rounded-lg border-2 border-emerald-200 focus:border-emerald-500 focus:outline-none text-sm font-bold text-gray-900 placeholder:text-gray-300 bg-white tabular-nums" data-id="element-1374" />
                      </div>
                      <button type="button" onClick={applyOtherDiscount} className="px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-black transition-colors" data-id="element-1375">
                        Aplicar
                      </button>
                    </div>
                  </div>
                  {discountAmount > 0 && <div className="flex items-center justify-between rounded-lg bg-emerald-100 border border-emerald-300 px-3 py-2" data-id="element-1376">
                      <span className="text-xs font-bold text-emerald-800" data-id="element-1377">
                        Descuento actual:{' '}
                        <span className="tabular-nums" data-id="element-1378">
                          -{formatCurrency(discountAmount)}
                        </span>
                      </span>
                      <button type="button" onClick={clearDiscount} className="text-[11px] font-black uppercase tracking-wider text-emerald-700 hover:text-emerald-900" data-id="element-1379">
                        Quitar
                      </button>
                    </div>}
                </div>}
            </div>

            {/* ─── Cash received + change (only for cash method) ─── */}
            {method === 'cash' && selectedItems.length > 0 && <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/40 p-4 space-y-3" data-id="element-1380">
                <div className="flex items-center justify-between" data-id="element-1381">
                  <p className="text-xs font-black text-amber-700 uppercase tracking-wider" data-id="element-1382">
                    💵 Paga con (efectivo)
                  </p>
                  <p className="text-[10px] text-amber-600 font-medium" data-id="element-1383">
                    opcional — calcula el vuelto
                  </p>
                </div>
                <div className="relative" data-id="element-1384">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-black text-amber-500 pointer-events-none" data-id="element-1385">
                    ₡
                  </span>
                  <input type="text" inputMode="decimal" value={cashReceived} onChange={e => setCashReceived(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="0" className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-amber-200 focus:border-amber-500 focus:outline-none text-2xl font-black text-gray-900 placeholder:text-gray-300 bg-white tabular-nums" data-id="element-1386" />
                </div>
                {quickCashAmounts.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-1387">
                    {quickCashAmounts.map(amt => <button key={amt} type="button" onClick={() => setCashReceived(String(amt))} className={cn('px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-colors tabular-nums', cashReceivedNum === amt ? 'border-amber-500 bg-amber-500 text-white' : 'border-amber-200 bg-white text-amber-700 hover:border-amber-400')} data-id="element-1388">
                        {formatCurrency(amt)}
                      </button>)}
                  </div>}
                {cashReceivedNum > 0 && <div className={cn('rounded-xl px-4 py-3 flex items-baseline justify-between', hasEnoughCash ? 'bg-emerald-500 text-white' : 'bg-red-50 border-2 border-red-200')} data-id="element-1389">
                    <span className={cn('text-xs font-black uppercase tracking-wider', hasEnoughCash ? 'text-white/90' : 'text-red-600')} data-id="element-1390">
                      {hasEnoughCash ? 'Vuelto a entregar' : 'Falta'}
                    </span>
                    <span className={cn('text-2xl font-black tabular-nums', hasEnoughCash ? 'text-white' : 'text-red-700')} data-id="element-1391">
                      {formatCurrency(hasEnoughCash ? change : Math.abs(change))}
                    </span>
                  </div>}
              </div>}

            {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-start gap-2" data-id="element-1392">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-1393" />
                {error}
              </div>}
          </>}
      </div>

      {unpaidItems.length > 0 && <div className="px-6 py-4 border-t border-gray-100 shrink-0 bg-gray-50 rounded-b-2xl" data-id="element-1394">
          {discountAmount > 0 && <div className="mb-3 space-y-1" data-id="element-1395">
              <div className="flex justify-between items-baseline text-xs" data-id="element-1396">
                <span className="font-bold text-gray-500 uppercase tracking-wider" data-id="element-1397">
                  Subtotal
                </span>
                <span className="font-bold text-gray-700 tabular-nums" data-id="element-1398">
                  {formatCurrency(total)}
                </span>
              </div>
              <div className="flex justify-between items-baseline text-xs" data-id="element-1399">
                <span className="font-bold text-emerald-600 uppercase tracking-wider" data-id="element-1400">
                  Descuento
                </span>
                <span className="font-black text-emerald-600 tabular-nums" data-id="element-1401">
                  -{formatCurrency(discountAmount)}
                </span>
              </div>
            </div>}
          <div className="flex justify-between items-baseline mb-3" data-id="element-1402">
            <div data-id="element-1403">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-1404">
                {isPartial ? 'Total parcial' : 'Total a cobrar'}
              </p>
              {isPartial && <p className="text-[10px] text-amber-600 font-bold" data-id="element-1405">
                  Cuenta dividida — quedará saldo pendiente
                </p>}
            </div>
            <span className="text-3xl font-black text-amber-500 tabular-nums" data-id="element-1406">
              {formatCurrency(totalAfterDiscount)}
            </span>
          </div>
          {hasIncompleteArmaPrices && <div className="mb-3 rounded-xl border-2 border-red-200 bg-red-50 px-3 py-2 flex items-start gap-2" data-id="element-1407">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-600" data-id="element-1408" />
              <p className="text-xs font-bold text-red-700 leading-snug" data-id="element-1409">
                Ingresa el precio del plato{' '}
                <span className="uppercase" data-id="element-1410">Arma tu plato</span> antes de
                cobrar.
              </p>
            </div>}
          <Button fullWidth size="lg" onClick={onSubmit} disabled={submitting || selectedItems.length === 0 || hasIncompleteArmaPrices || hasInvalidSplit} className="bg-gray-900 hover:bg-gray-800 text-white font-bold shadow-lg" data-id="element-1411">
            {submitting ? <span className="flex items-center justify-center gap-2" data-id="element-1412">
                <Loader2 className="w-5 h-5 animate-spin" data-id="element-1413" />
                Procesando...
              </span> : <span className="flex items-center justify-center gap-2" data-id="element-1414">
                <Banknote className="w-5 h-5" data-id="element-1415" />
                Cobrar
              </span>}
          </Button>
        </div>}
    </>;
}
// ─── STEP 2: Payment confirmed — choose: invoice or close ─────────────────
function PaidStep(props: {
  payment: PaymentRecord;
  allPaid: boolean;
  autoInvoiceResult?: {
    ok: boolean;
    msg: string;
    email: string;
  } | null;
  autoInvoiceSending?: boolean;
  isRancho?: boolean;
  onPrintComanda?: () => void;
  printingComanda?: boolean;
  printComandaResult?: {
    ok: boolean;
    msg: string;
  } | null;
  onClose: () => void;
  onGoInvoice: () => void;
  onChargeRemaining: () => void;
}) {
  const {
    payment,
    allPaid,
    autoInvoiceResult,
    autoInvoiceSending,
    isRancho,
    onPrintComanda,
    printingComanda,
    printComandaResult,
    onClose,
    onGoInvoice,
    onChargeRemaining
  } = props;
  const methodLabel = payment.method === 'cash' ? 'Efectivo' : payment.method === 'sinpe' ? 'SINPE Móvil' : 'Tarjeta';
  return <>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-1416">
        <div className="flex items-center gap-3" data-id="element-1417">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-1418">
            <CheckCircle2 className="w-6 h-6 text-emerald-500" data-id="element-1419" />
          </div>
          <div data-id="element-1420">
            <h2 className="text-xl font-black text-gray-900" data-id="element-1421">
              Pago registrado
            </h2>
            <p className="text-xs text-gray-500" data-id="element-1422">
              {methodLabel} · {formatCurrency(payment.amount)}
              {!allPaid && ' · Saldo pendiente en mesa'}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1423">
          <X className="w-5 h-5 text-gray-500" data-id="element-1424" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-8" data-id="element-1425">
        <div className="text-center mb-6" data-id="element-1426">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-50 mb-4" data-id="element-1427">
            <CheckCircle2 className="w-12 h-12 text-emerald-500" data-id="element-1428" />
          </div>
          <h3 className="text-2xl font-black text-gray-900 mb-1" data-id="element-1429">
            ¡Cobro exitoso!
          </h3>
          {autoInvoiceSending ? <p className="text-sm text-gray-500 flex items-center justify-center gap-2" data-id="element-1430">
              <Loader2 className="w-4 h-4 animate-spin" data-id="element-1431" />
              Enviando factura por correo…
            </p> : autoInvoiceResult ? <p className={cn('text-sm font-medium', autoInvoiceResult.ok ? 'text-emerald-600' : 'text-amber-700')} data-id="element-1432">
              {autoInvoiceResult.ok ? '✓ ' : '⚠ '}
              {autoInvoiceResult.msg}
            </p> : <p className="text-sm text-gray-500" data-id="element-1433">¿El cliente desea factura?</p>}
        </div>

        {autoInvoiceResult?.ok && <div className="max-w-sm mx-auto mb-4 rounded-xl px-4 py-3 text-sm font-medium border bg-emerald-50 border-emerald-200 text-emerald-700 text-center" data-id="element-1434">
            Comprobante enviado a{' '}
            <span className="font-bold" data-id="element-1435">{autoInvoiceResult.email}</span>.
          </div>}

        <div className="max-w-sm mx-auto space-y-2" data-id="element-1436">
          {isRancho && onPrintComanda ? <>
              <button onClick={onPrintComanda} disabled={printingComanda} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-bold transition-colors disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-1437">
                {printingComanda ? <>
                    <Loader2 className="w-5 h-5 animate-spin" data-id="element-1438" />
                    Enviando…
                  </> : <>
                    <Printer className="w-5 h-5" data-id="element-1439" />
                    Imprimir comanda
                  </>}
              </button>
              {printComandaResult && <div className={cn('rounded-xl px-3 py-2 text-xs font-bold text-center', printComandaResult.ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200')} data-id="element-1440">
                  {printComandaResult.msg}
                </div>}
            </> : <button onClick={onGoInvoice} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-bold transition-colors" data-id="element-1441">
              <FileText className="w-5 h-5" data-id="element-1442" />
              Sí, generar factura
            </button>}
          {!allPaid ? <button onClick={onChargeRemaining} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold transition-colors" data-id="element-1443">
              <ArrowLeft className="w-4 h-4" data-id="element-1444" />
              Cobrar saldo restante
            </button> : <button onClick={onClose} className="w-full py-3 rounded-xl border-2 border-gray-200 hover:border-gray-300 text-gray-700 font-bold transition-colors" data-id="element-1445">
              {isRancho ? 'Cerrar' : 'No, cerrar'}
            </button>}
        </div>
      </div>
    </>;
}
// ─── STEP 3: Generate invoice — collect customer data + send by email ────
function InvoiceStep(props: {
  table: TableWithOrder;
  billingInfo: BillingInfo;
  payment: PaymentRecord;
  customerName: string;
  setCustomerName: (s: string) => void;
  customerLegalId: string;
  setCustomerLegalId: (s: string) => void;
  customerEmail: string;
  setCustomerEmail: (s: string) => void;
  onBack: () => void;
  onClose: () => void;
}) {
  const {
    table,
    billingInfo,
    payment,
    customerName,
    setCustomerName,
    customerLegalId,
    setCustomerLegalId,
    customerEmail,
    setCustomerEmail,
    onBack,
    onClose
  } = props;
  const consecStr = String(payment.consecutive_number).padStart(6, '0');
  const invoiceUrl = `${window.location.origin}/factura/${payment.id}`;
  const buildEmailBody = () => {
    const greeting = customerName.trim() || 'Cliente';
    return `Estimad@ ${greeting},\n\n` + `Agradecemos profundamente su visita. Esperamos que su paso por nuestras instalaciones haya sido de su agrado.\n\n` + (customerLegalId.trim() ? `Datos del cliente:\n` + `Nombre: ${greeting}\n` + `Cédula / Identificación: ${customerLegalId.trim()}\n\n` : '') + `Adjunto podrá encontrar la factura solicitada:\n` + `${invoiceUrl}\n\n` + `Factura N° ${consecStr} · Total: ${new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0
    }).format(payment.amount)}\n\n` + `¡Lo esperamos pronto!\n\n` + `Att: ${billingInfo.business_name}`;
  };
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{
    ok: boolean;
    msg: string;
  } | null>(null);
  const handleSendEmail = async () => {
    if (!customerEmail.trim()) {
      alert('Ingresa el correo electrónico del cliente.');
      return;
    }
    // ─── Defensive validation ─────────────────────────────────────────
    // payment viene de InvoiceStep props → completedPayment → result.payment
    // de processPayment(), que hace INSERT INTO payments ... RETURNING *.
    // Por lo tanto payment.id ES el UUID de payments.id (NO order.id, NO
    // table.id, NO consecutive_number).
    const paymentId = payment?.id;
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    // Logs temporales para validar exactamente qué se manda
    console.log('[NOIZA][invoice] payment object:', payment);
    console.log('[NOIZA][invoice] payment_id being sent:', paymentId);
    console.log('[NOIZA][invoice] payment_id is valid UUID?', UUID_RE.test(String(paymentId || '')));
    if (!paymentId || !UUID_RE.test(String(paymentId))) {
      setSendResult({
        ok: false,
        msg: `payment_id inválido (${paymentId}). Debería ser el UUID de payments.id. Reportá este caso.`
      });
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const {
        supabase
      } = await import('../../lib/supabase');
      const {
        data,
        error
      } = await supabase.functions.invoke('send-invoice-email', {
        body: {
          payment_id: paymentId,
          customer_email: customerEmail.trim(),
          customer_name: customerName.trim() || null,
          customer_legal_id: customerLegalId.trim() || null
        }
      });
      console.log('[NOIZA][invoice] invoke response:', {
        data,
        error
      });
      if (error) {
        // FunctionsHttpError exposes .context with the response body
        let serverError = error.message || 'Error invocando la función';
        try {
          const ctx: any = (error as any).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            serverError = body?.error || body?.message || serverError;
          } else if (ctx?.error) {
            serverError = ctx.error;
          }
        } catch {
          /* swallow */
        }
        setSendResult({
          ok: false,
          msg: `No se pudo enviar: ${serverError}. Revisá los logs de la edge function en Supabase.`
        });
        setSending(false);
        return;
      }
      if (data?.success) {
        setSendResult({
          ok: true,
          msg: `✓ Comprobante enviado a ${customerEmail.trim()}. Revisá la bandeja de entrada (y la carpeta de Spam si tarda).`
        });
        setSending(false);
        return;
      }
      setSendResult({
        ok: false,
        msg: `Respuesta inesperada: ${JSON.stringify(data)}`
      });
    } catch (err: any) {
      console.error('[NOIZA][invoice] handleSendEmail exception:', err);
      setSendResult({
        ok: false,
        msg: `Error: ${err?.message || 'desconocido'}`
      });
    } finally {
      setSending(false);
    }
  };
  const handlePrint = () => {
    openPrintableInvoice({
      info: billingInfo,
      payment,
      tableLabel: table.table_name || `Mesa ${table.table_number}`,
      customer: {
        name: customerName.trim() || null,
        legal_id: customerLegalId.trim() || null,
        email: customerEmail.trim() || null
      }
    });
  };
  const canSend = customerName.trim() && customerEmail.trim();
  return <>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0" data-id="element-1446">
        <div className="flex items-center gap-3" data-id="element-1447">
          <button onClick={onBack} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1448">
            <ArrowLeft className="w-4 h-4 text-gray-600" data-id="element-1449" />
          </button>
          <div data-id="element-1450">
            <h2 className="text-xl font-black text-gray-900 flex items-center gap-2" data-id="element-1451">
              <FileText className="w-5 h-5 text-gray-700" data-id="element-1452" />
              Generar factura
            </h2>
            <p className="text-xs text-gray-500" data-id="element-1453">
              Factura N° {consecStr} · {formatCurrency(payment.amount)}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1454">
          <X className="w-5 h-5 text-gray-500" data-id="element-1455" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-id="element-1456">
        <p className="text-xs text-gray-500 leading-relaxed bg-gray-50 border border-gray-100 rounded-xl px-4 py-3" data-id="element-1457">
          Ingresa los datos del cliente para emitir la factura. Se enviará por
          correo electrónico con un enlace al comprobante.
        </p>

        <div data-id="element-1458">
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-1459">
            Nombre completo *
          </label>
          <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Ej. María Pérez Rojas" className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-100 focus:border-amber-400 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1460" />
        </div>

        <div data-id="element-1461">
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-1462">
            Cédula / Identificación
            <span className="text-gray-400 font-medium normal-case ml-1" data-id="element-1463">
              (opcional)
            </span>
          </label>
          <input type="text" value={customerLegalId} onChange={e => setCustomerLegalId(e.target.value)} placeholder="Ej. 1-1234-5678" className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-100 focus:border-amber-400 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1464" />
        </div>

        <div data-id="element-1465">
          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5" data-id="element-1466">
            Correo electrónico *
          </label>
          <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="cliente@correo.com" className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-100 focus:border-amber-400 focus:outline-none text-sm font-medium text-gray-900 placeholder:text-gray-400 bg-white" data-id="element-1467" />
        </div>

        {sendResult && <div className={cn('rounded-xl px-4 py-3 text-sm font-medium border', sendResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700')} data-id="element-1468">
            {sendResult.msg}
          </div>}
      </div>

      <div className="px-6 py-4 border-t border-gray-100 shrink-0 bg-gray-50 rounded-b-2xl flex flex-col sm:flex-row gap-2" data-id="element-1469">
        <Button variant="outline" onClick={handlePrint} className="flex-1 flex items-center justify-center gap-2 font-bold" data-id="element-1470">
          <Printer className="w-4 h-4" data-id="element-1471" />
          Imprimir
        </Button>
        <Button onClick={handleSendEmail} disabled={!canSend || sending} className="flex-1 bg-gray-900 hover:bg-gray-800 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1472">
          {sending ? <>
              <Loader2 className="w-4 h-4 animate-spin" data-id="element-1473" />
              Enviando…
            </> : <>
              <Mail className="w-4 h-4" data-id="element-1474" />
              Enviar por correo
            </>}
        </Button>
      </div>
    </>;
}